import React, { useState, useEffect, useMemo } from 'react';
import { getDocs, doc, setDoc, updateDoc, query, where } from 'firebase/firestore';
import { db } from '../../firebase/config/firebase';
import { bookingsCol, vehiclesCol } from '../../firebase/firestore/collections';
import { Trip, University, TripDirection, TripStatus, Booking, Vehicle } from '../../types';
import { SEED_VEHICLES } from '../../constants/mockSeed';
import { tripService } from '../../services/tripService';
import { universityService } from '../../services/universityService';
import { adminBookingService } from '../../services/adminBookingService';
import {
  Bus,
  Plus,
  Search,
  Calendar,
  CheckCircle2,
  Armchair,
  RefreshCw,
  X,
  Repeat,
  ArrowRight,
  Eye,
  Printer,
  Phone,
  MessageSquare,
  Users,
  FileText,
  Trash2,
  Sparkles,
  MapPin,
  Wrench,
  RotateCcw,
  Edit,
} from 'lucide-react';
import './AdminPages.css';
import { DEFAULT_LAUNCH_STATION } from '../../constants/tripRoutes';
import { AdminLoadMore } from './AdminLoadMore';
import { printTripManifest } from '../../utils/manifestPrinter';
import { BulkRouteTripsModal } from './trips/BulkRouteTripsModal';

const ARABIC_MONTH_NAMES = [
  'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
  'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر',
];

const WEEKDAYS_CONFIG = [
  { label: 'السبت', short: 'سبت', value: 6 },
  { label: 'الأحد', short: 'أحد', value: 0 },
  { label: 'الإثنين', short: 'إثن', value: 1 },
  { label: 'الثلاثاء', short: 'ثلا', value: 2 },
  { label: 'الأربعاء', short: 'أرب', value: 3 },
  { label: 'الخميس', short: 'خمي', value: 4 },
  { label: 'الجمعة', short: 'جمع', value: 5 },
];

const getArabicMonthTitle = (monthStr: string) => {
  if (!monthStr) return '';
  const parts = monthStr.split('-');
  const y = parts[0];
  const m = Number(parts[1]);
  return `${ARABIC_MONTH_NAMES[m - 1] || ''} ${y}`;
};

export interface TripPassengerEntry {
  seatNumber: number;
  booking?: Booking;
  studentName?: string;
  studentPhone?: string;
  bookingCode?: string;
  ticketId?: string;
  paymentStatus?: string;
  pickupStation?: string;
  attendance: 'BOARDED' | 'NO_SHOW' | 'PENDING';
}

export interface VehicleRowLayout {
  rowNum: number;
  left: number[];
  right: number[];
}

export interface VehicleLayoutPlan {
  type: 'VAN' | 'MINIBUS' | 'COACH';
  typeNameAr: string;
  rows: VehicleRowLayout[];
  rearBench: number[];
}

export function getVehicleSeatLayout(capacity: number): VehicleLayoutPlan {
  const cap = Math.max(1, Number(capacity) || 50);

  if (cap <= 16) {
    // Van / HiAce (ميكروباص / تويوتا هاي اس) (e.g. 10 to 16 seats)
    // 3 seats per standard row (2 left + aisle + 1 right), rear bench takes 4 seats
    const rearBenchSize = cap >= 7 ? 4 : Math.min(3, cap);
    const standardSeatsCount = cap - rearBenchSize;
    const rowCount = Math.ceil(standardSeatsCount / 3);
    const rows: VehicleRowLayout[] = [];

    let currentSeat = 1;
    for (let r = 1; r <= rowCount; r++) {
      const remainingInStandard = standardSeatsCount - (currentSeat - 1);
      const seatsInThisRow = Math.min(3, remainingInStandard);

      let leftSeats: number[] = [];
      let rightSeats: number[] = [];

      if (seatsInThisRow === 3) {
        leftSeats = [currentSeat, currentSeat + 1];
        rightSeats = [currentSeat + 2];
        currentSeat += 3;
      } else if (seatsInThisRow === 2) {
        leftSeats = [currentSeat, currentSeat + 1];
        rightSeats = [];
        currentSeat += 2;
      } else if (seatsInThisRow === 1) {
        leftSeats = [currentSeat];
        rightSeats = [];
        currentSeat += 1;
      }

      rows.push({
        rowNum: r,
        left: leftSeats,
        right: rightSeats,
      });
    }

    const rearSeats: number[] = [];
    for (let s = currentSeat; s <= cap; s++) {
      rearSeats.push(s);
    }

    return {
      type: 'VAN',
      typeNameAr: 'الميكروباص / هاي اس',
      rows,
      rearBench: rearSeats,
    };
  } else if (cap <= 32) {
    // Minibus / Coaster (17 to 32 seats)
    // 3 seats per standard row (1 left + aisle + 2 right), rear bench takes 4 seats
    const rearBenchSize = 4;
    const standardSeatsCount = cap - rearBenchSize;
    const rowCount = Math.ceil(standardSeatsCount / 3);
    const rows: VehicleRowLayout[] = [];

    let currentSeat = 1;
    for (let r = 1; r <= rowCount; r++) {
      const remainingInStandard = standardSeatsCount - (currentSeat - 1);
      const seatsInThisRow = Math.min(3, remainingInStandard);

      let leftSeats: number[] = [];
      let rightSeats: number[] = [];

      if (seatsInThisRow === 3) {
        leftSeats = [currentSeat];
        rightSeats = [currentSeat + 1, currentSeat + 2];
        currentSeat += 3;
      } else if (seatsInThisRow === 2) {
        leftSeats = [currentSeat];
        rightSeats = [currentSeat + 1];
        currentSeat += 2;
      } else if (seatsInThisRow === 1) {
        leftSeats = [currentSeat];
        rightSeats = [];
        currentSeat += 1;
      }

      rows.push({
        rowNum: r,
        left: leftSeats,
        right: rightSeats,
      });
    }

    const rearSeats: number[] = [];
    for (let s = currentSeat; s <= cap; s++) {
      rearSeats.push(s);
    }

    return {
      type: 'MINIBUS',
      typeNameAr: 'الميني باص / كوستر',
      rows,
      rearBench: rearSeats,
    };
  } else {
    // Large Coach Bus (33 to 50+ seats)
    // 4 seats per standard row (2 left + aisle + 2 right), rear bench takes remainder
    const rearBenchSize = cap % 4 === 2 ? 2 : cap % 4 === 0 ? 4 : cap % 4;
    const standardSeatsCount = cap - rearBenchSize;
    const rowCount = Math.floor(standardSeatsCount / 4);
    const rows: VehicleRowLayout[] = [];

    let currentSeat = 1;
    for (let r = 1; r <= rowCount; r++) {
      rows.push({
        rowNum: r,
        left: [currentSeat, currentSeat + 1],
        right: [currentSeat + 2, currentSeat + 3],
      });
      currentSeat += 4;
    }

    const rearSeats: number[] = [];
    for (let s = currentSeat; s <= cap; s++) {
      rearSeats.push(s);
    }

    return {
      type: 'COACH',
      typeNameAr: 'الحافلة السياحية',
      rows,
      rearBench: rearSeats,
    };
  }
}

export const formatArabicFullDate = (dateStr: string): string => {
  if (!dateStr) return '';
  try {
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const y = Number(parts[0]);
      const m = Number(parts[1]);
      const d = Number(parts[2]);
      const dt = new Date(y, m - 1, d);
      const dayNames = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
      const dayName = dayNames[dt.getDay()] || '';
      const monthName = ARABIC_MONTH_NAMES[m - 1] || '';
      return `${dayName} ${d} ${monthName} ${y}`;
    }
  } catch {}
  return dateStr;
};

export interface UniversityTheme {
  name: string;
  badgeBg: string;
  badgeText: string;
  badgeBorder: string;
  accentBar: string;
  dotColor: string;
}

// Fixed university sequence for display sorting:
// Mansoura -> Kafr El Sheikh -> Sadat -> Tanta -> Damanhour -> Damietta -> Horus -> Alamein/Matrouh -> Menoufia -> Other
export const UNIVERSITY_ORDER: { key: string; name: string; priority: number }[] = [
  { key: 'منصورة', name: 'جامعة المنصورة', priority: 1 },
  { key: 'كفر الشيخ', name: 'جامعة كفر الشيخ', priority: 2 },
  { key: 'سادات', name: 'جامعة مدينة السادات', priority: 3 },
  { key: 'ريادة', name: 'جامعة الريادة', priority: 3 },
  { key: 'طنطا', name: 'جامعة طنطا', priority: 4 },
  { key: 'دمنهور', name: 'جامعة دمنهور', priority: 5 },
  { key: 'دمياط', name: 'جامعة دمياط', priority: 6 },
  { key: 'حورس', name: 'جامعة حورس', priority: 7 },
  { key: 'علمين', name: 'جامعة العلمين', priority: 8 },
  { key: 'مطروح', name: 'جامعة مطروح', priority: 9 },
  { key: 'منوفية', name: 'جامعة المنوفية', priority: 10 },
];

export const getUniversitySortIndex = (name?: string): number => {
  if (!name) return 99;
  const match = UNIVERSITY_ORDER.find((item) => name.includes(item.key));
  return match ? match.priority : 50;
};

export const getUniversityTheme = (name?: string): UniversityTheme => {
  const n = name || '';
  if (n.includes('منصورة')) {
    return {
      name: 'جامعة المنصورة',
      badgeBg: '#EFF6FF',
      badgeText: '#1D4ED8',
      badgeBorder: '#BFDBFE',
      accentBar: '#2563EB',
      dotColor: '#3B82F6',
    };
  }
  if (n.includes('كفر الشيخ')) {
    return {
      name: 'جامعة كفر الشيخ',
      badgeBg: '#ECFDF5',
      badgeText: '#047857',
      badgeBorder: '#A7F3D0',
      accentBar: '#059669',
      dotColor: '#10B981',
    };
  }
  if (n.includes('سادات') || n.includes('ريادة')) {
    return {
      name: 'جامعة السادات والريادة',
      badgeBg: '#F5F3FF',
      badgeText: '#6D28D9',
      badgeBorder: '#DDD6FE',
      accentBar: '#7C3AED',
      dotColor: '#8B5CF6',
    };
  }
  if (n.includes('طنطا')) {
    return {
      name: 'جامعة طنطا',
      badgeBg: '#EEF2FF',
      badgeText: '#4338CA',
      badgeBorder: '#C7D2FE',
      accentBar: '#4F46E5',
      dotColor: '#6366F1',
    };
  }
  if (n.includes('دمنهور')) {
    return {
      name: 'جامعة دمنهور',
      badgeBg: '#F0FDFA',
      badgeText: '#0F766E',
      badgeBorder: '#99F6E4',
      accentBar: '#0D9488',
      dotColor: '#14B8A6',
    };
  }
  if (n.includes('دمياط')) {
    return {
      name: 'جامعة دمياط',
      badgeBg: '#FFFBEB',
      badgeText: '#B45309',
      badgeBorder: '#FDE68A',
      accentBar: '#D97706',
      dotColor: '#F59E0B',
    };
  }
  if (n.includes('حورس')) {
    return {
      name: 'جامعة حورس',
      badgeBg: '#FFF1F2',
      badgeText: '#BE123C',
      badgeBorder: '#FECDD3',
      accentBar: '#E11D48',
      dotColor: '#F43F5E',
    };
  }
  if (n.includes('علمين') || n.includes('مطروح')) {
    return {
      name: 'العلمين ومطروح',
      badgeBg: '#F0F9FF',
      badgeText: '#0369A1',
      badgeBorder: '#BAE6FD',
      accentBar: '#0284C7',
      dotColor: '#0EA5E9',
    };
  }
  if (n.includes('منوفية')) {
    return {
      name: 'جامعة المنوفية',
      badgeBg: '#FDF4FF',
      badgeText: '#A21CAF',
      badgeBorder: '#F5D0FE',
      accentBar: '#C026D3',
      dotColor: '#D946EF',
    };
  }
  return {
    name: n || 'جامعة معتمدة',
    badgeBg: '#F8FAFC',
    badgeText: '#334155',
    badgeBorder: '#E2E8F0',
    accentBar: '#64748B',
    dotColor: '#94A3B8',
  };
};

export const AdminTrips: React.FC = () => {
  const [trips, setTrips] = useState<Trip[]>([]);
  const [universities, setUniversities] = useState<University[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUniFilter, setSelectedUniFilter] = useState('ALL');
  const [selectedDirFilter, setSelectedDirFilter] = useState('ALL');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('ALL');
  const [selectedStationFilter, setSelectedStationFilter] = useState('ALL');
  const [visibleCount, setVisibleCount] = useState<number>(15);
  const [tripTab, setTripTab] = useState<'UPCOMING' | 'COMPLETED'>('UPCOMING');

  // Modals & Manifest Interactive State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [editingTrip, setEditingTrip] = useState<Trip | null>(null);
  const [editPrice, setEditPrice] = useState<number | ''>(150);
  const [editDate, setEditDate] = useState<string>('');
  const [editDepTime, setEditDepTime] = useState<string>('07:00');
  const [editArrTime, setEditArrTime] = useState<string>('09:30');
  const [editDirection, setEditDirection] = useState<TripDirection>('OUTBOUND');
  const [editOrigin, setEditOrigin] = useState<string>('');
  const [editDestination, setEditDestination] = useState<string>('');
  const [editVehicleId, setEditVehicleId] = useState<string>('');
  const [editDriverName, setEditDriverName] = useState<string>('');
  const [editDriverPhone, setEditDriverPhone] = useState<string>('');
  const [editStatus, setEditStatus] = useState<TripStatus>('OPEN');
  const [selectedTripManifest, setSelectedTripManifest] = useState<{
    trip: Trip;
    seats: any[];
    passengers: TripPassengerEntry[];
  } | null>(null);
  const [selectedManifestSeat, setSelectedManifestSeat] = useState<number | null>(null);
  const [manifestFilter, setManifestFilter] = useState<'ALL' | 'BOOKED' | 'AVAILABLE' | 'BOARDED' | 'NO_SHOW'>('ALL');
  const [manifestSearchTerm, setManifestSearchTerm] = useState<string>('');
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [tripBookingsCountMap, setTripBookingsCountMap] = useState<Record<string, number>>({});


  // New trip form state
  const [newUniId, setNewUniId] = useState('');
  const [newDirection, setNewDirection] = useState<TripDirection>('OUTBOUND');
  const [newDepTime, setNewDepTime] = useState('07:00');
  const [newArrTime, setNewArrTime] = useState('09:30');
  const [newVehicleId, setNewVehicleId] = useState('veh_01');
  const [newPrice, setNewPrice] = useState(150);
  const [newLaunchStation, setNewLaunchStation] = useState<string>(DEFAULT_LAUNCH_STATION);
  const [customStationName, setCustomStationName] = useState<string>('');

  // Calendar Multi-Day Monthly Travel Selection
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [selectedDaysOfMonth, setSelectedDaysOfMonth] = useState<number[]>(() => [new Date().getDate()]);

  const toggleDayOfMonth = (day: number) => {
    setSelectedDaysOfMonth((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort((a, b) => a - b)
    );
  };

  const toggleAllMatchingWeekday = (weekdayValue: number) => {
    if (!selectedMonth) return;
    const parts = selectedMonth.split('-');
    const year = Number(parts[0]);
    const month = Number(parts[1]);
    const totalDays = new Date(year, month, 0).getDate();

    const daysWithThisWeekday: number[] = [];
    for (let d = 1; d <= totalDays; d++) {
      if (new Date(year, month - 1, d).getDay() === weekdayValue) {
        daysWithThisWeekday.push(d);
      }
    }

    const allSelected =
      daysWithThisWeekday.length > 0 &&
      daysWithThisWeekday.every((d) => selectedDaysOfMonth.includes(d));

    if (allSelected) {
      setSelectedDaysOfMonth((prev) => prev.filter((d) => !daysWithThisWeekday.includes(d)));
    } else {
      setSelectedDaysOfMonth((prev) => {
        const set = new Set([...prev, ...daysWithThisWeekday]);
        return Array.from(set).sort((a, b) => a - b);
      });
    }
  };

  const selectAllStudyDays = () => {
    if (!selectedMonth) return;
    const parts = selectedMonth.split('-');
    const year = Number(parts[0]);
    const month = Number(parts[1]);
    const totalDays = new Date(year, month, 0).getDate();
    const studyDays: number[] = [];
    for (let d = 1; d <= totalDays; d++) {
      const dayOfWeek = new Date(year, month - 1, d).getDay();
      if (dayOfWeek !== 5) {
        // Exclude Friday
        studyDays.push(d);
      }
    }
    setSelectedDaysOfMonth(studyDays);
  };

  // Load trips, universities & vehicles with blazing fast in-memory caching
  const loadData = async (forceRefresh = false) => {
    setIsLoading(true);
    try {
      const [allTripsList, unis, vehsSnap, allBookingsList] = await Promise.all([
        adminBookingService.getAllTrips(forceRefresh),
        universityService.getAllUniversities(forceRefresh),
        getDocs(vehiclesCol).catch(() => null),
        adminBookingService.getAllBookings(forceRefresh).catch(() => []),
      ]);

      setTrips(allTripsList);
      setUniversities(unis);

      const bMap: Record<string, number> = {};
      (allBookingsList || []).forEach((b) => {
        const isCancelled =
          b.status === 'CANCELLED' ||
          b.bookingStatus === 'CANCELLED' ||
          b.status === 'POSTPONED' ||
          b.bookingStatus === 'POSTPONED' ||
          b.status === 'DELETED' ||
          b.bookingStatus === 'DELETED' ||
          (b.paymentStatus as string) === 'REJECTED' ||
          (b.paymentStatus as string) === 'CANCELLED';

        if (!isCancelled && b.tripId) {
          bMap[b.tripId] = (bMap[b.tripId] || 0) + 1;
        }
      });
      setTripBookingsCountMap(bMap);

      if (vehsSnap && !vehsSnap.empty) {
        setVehicles(vehsSnap.docs.map((d) => ({ ...d.data(), id: d.id })) as Vehicle[]);
      } else {
        setVehicles(SEED_VEHICLES);
      }
    } catch (err) {
      console.warn('Falling back to local seed data for trips and vehicles:', err);
      const allTripsList = await adminBookingService.getAllTrips();
      setTrips(allTripsList);
      const unis = await universityService.getAllUniversities();
      setUniversities(unis);
      setVehicles(SEED_VEHICLES);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Selected vehicle & dynamic seat capacity for trip scheduling
  const selectedVehicle = useMemo(() => {
    return (
      vehicles.find((v) => v.id === newVehicleId) ||
      SEED_VEHICLES.find((v) => v.id === newVehicleId) ||
      vehicles[0] ||
      SEED_VEHICLES[0]
    );
  }, [vehicles, newVehicleId]);

  const selectedVehicleCapacity = selectedVehicle?.capacity || 50;

  // Tab Counts
  const tripCounts = useMemo(() => {
    const upcoming = trips.filter((t) => t.status !== 'COMPLETED' && t.status !== 'DEPARTED').length;
    const completed = trips.filter((t) => t.status === 'COMPLETED' || t.status === 'DEPARTED').length;
    return { upcoming, completed };
  }, [trips]);

  // Filtered trips (divided into Upcoming vs Completed/Departed registers with multi-tier sorting)
  const filteredTrips = useMemo(() => {
    const list = trips.filter((t) => {
      // 1. Tab filter
      const matchesTab =
        tripTab === 'UPCOMING'
          ? t.status !== 'COMPLETED' && t.status !== 'DEPARTED'
          : t.status === 'COMPLETED' || t.status === 'DEPARTED';

      if (!matchesTab) return false;

      // 2. Search filter
      const matchesSearch =
        searchTerm === '' ||
        t.tripCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.universityName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.date?.includes(searchTerm) ||
        t.departureTime?.includes(searchTerm) ||
        t.driverName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.vehiclePlate?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (t.origin && t.origin.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (t.destination && t.destination.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesUni = selectedUniFilter === 'ALL' || t.universityId === selectedUniFilter;
      const matchesDir =
        selectedDirFilter === 'ALL' ||
        (selectedDirFilter === 'OUTBOUND' && (!t.direction || t.direction === 'OUTBOUND')) ||
        (selectedDirFilter === 'RETURN' && t.direction === 'RETURN');
      const matchesStatus = selectedStatusFilter === 'ALL' || t.status === selectedStatusFilter;
      const matchesStation =
        selectedStationFilter === 'ALL' ||
        (t.origin && t.origin.includes(selectedStationFilter)) ||
        (t.destination && t.destination.includes(selectedStationFilter));

      return matchesSearch && matchesUni && matchesDir && matchesStatus && matchesStation;
    });

    // Multi-tier sort according to requested schedule order:
    // 1. Date ascending (today -> tomorrow -> next day)
    // 2. Direction: OUTBOUND (0) first, then RETURN (1) for that same day
    // 3. University order: Mansoura -> Kafr El Sheikh -> Sadat -> Tanta -> Damanhour -> Damietta -> Horus...
    // 4. Departure Time ascending
    // 5. Trip code
    return list.sort((a, b) => {
      const dateA = a.date || '';
      const dateB = b.date || '';
      if (dateA !== dateB) {
        return dateA.localeCompare(dateB);
      }

      const dirA = a.direction === 'RETURN' ? 1 : 0;
      const dirB = b.direction === 'RETURN' ? 1 : 0;
      if (dirA !== dirB) {
        return dirA - dirB;
      }

      const uniA = getUniversitySortIndex(a.universityName);
      const uniB = getUniversitySortIndex(b.universityName);
      if (uniA !== uniB) {
        return uniA - uniB;
      }

      const timeA = a.departureTime || '';
      const timeB = b.departureTime || '';
      if (timeA !== timeB) {
        return timeA.localeCompare(timeB);
      }

      return (a.tripCode || '').localeCompare(b.tripCode || '');
    });
  }, [trips, tripTab, searchTerm, selectedUniFilter, selectedDirFilter, selectedStatusFilter, selectedStationFilter]);

  // Sliced trips for display (15 initial + 10 more on button click)
  const visibleTrips = useMemo(() => {
    return filteredTrips.slice(0, visibleCount);
  }, [filteredTrips, visibleCount]);

  // Reset visible count when filters or active tab change
  useEffect(() => {
    setVisibleCount(15);
  }, [tripTab, searchTerm, selectedUniFilter, selectedDirFilter, selectedStatusFilter, selectedStationFilter]);

  // Stats
  const stats = useMemo(() => {
    const total = trips.length;
    const upcoming = trips.filter((t) => t.status !== 'COMPLETED' && t.status !== 'DEPARTED').length;
    const completed = trips.filter((t) => t.status === 'COMPLETED' || t.status === 'DEPARTED').length;
    const open = trips.filter((t) => t.status === 'OPEN').length;
    const full = trips.filter((t) => t.status === 'FULL').length;
    const today = new Date().toISOString().split('T')[0];
    const todayTrips = trips.filter((t) => t.date === today).length;
    return { total, upcoming, completed, open, full, todayTrips };
  }, [trips]);

  // Open Create Modal
  const handleOpenCreate = () => {
    const defaultUni = universities[0]?.id || '';
    const defaultVehId = vehicles[0]?.id || SEED_VEHICLES[0]?.id || 'veh_01';
    setNewUniId(defaultUni);
    setNewDirection('OUTBOUND');
    setNewDepTime('07:00');
    setNewArrTime('09:30');
    setNewVehicleId(defaultVehId);
    setNewPrice(150);
    setNewLaunchStation(DEFAULT_LAUNCH_STATION);
    setCustomStationName('');
    const d = new Date();
    setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    setSelectedDaysOfMonth([d.getDate()]);
    setIsCreateModalOpen(true);
  };

  // Open Edit Trip Modal
  const handleOpenEdit = (trip: Trip) => {
    setEditingTrip(trip);
    setEditPrice(typeof trip.price === 'number' ? trip.price : 150);
    setEditDate(trip.date || '');
    setEditDepTime(trip.departureTime || '07:00');
    setEditArrTime(trip.expectedArrivalTime || trip.arrivalTime || '09:30');
    setEditDirection(trip.direction === 'RETURN' ? 'RETURN' : 'OUTBOUND');
    setEditOrigin(trip.origin || trip.pickupLocation || DEFAULT_LAUNCH_STATION);
    setEditDestination(trip.destination || trip.destinationDetails || trip.universityName || '');
    setEditVehicleId(trip.vehicleId || vehicles[0]?.id || 'veh_01');
    setEditDriverName(trip.driverName || '');
    setEditDriverPhone(trip.driverPhone || '');
    setEditStatus(trip.status || 'OPEN');
    setIsEditModalOpen(true);
  };

  // Save Edited Trip
  const handleSaveEditTrip = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTrip) return;

    if (!editDate) {
      alert('يرجى تحديد تاريخ الرحلة.');
      return;
    }
    if (!editDepTime) {
      alert('يرجى تحديد موعد التحرك / الإقلاع.');
      return;
    }
    const numPrice = Number(editPrice);
    if (isNaN(numPrice) || numPrice <= 0) {
      alert('يرجى إدخال سعر صحيح للتذكرة.');
      return;
    }

    setActionLoading(true);
    try {
      const selectedVeh = vehicles.find((v) => v.id === editVehicleId);
      const updates: Partial<Trip> = {
        price: numPrice,
        date: editDate,
        departureTime: editDepTime,
        expectedArrivalTime: editArrTime,
        direction: editDirection,
        origin: editOrigin.trim(),
        destination: editDestination.trim(),
        vehicleId: editVehicleId || editingTrip.vehicleId,
        vehiclePlate: selectedVeh?.vehicleNumber || selectedVeh?.plateNumber || editingTrip.vehiclePlate,
        vehicleNumber: selectedVeh?.vehicleNumber || selectedVeh?.plateNumber || editingTrip.vehicleNumber,
        driverName: editDriverName.trim() || selectedVeh?.driverName || editingTrip.driverName || '',
        driverPhone: editDriverPhone.trim() || selectedVeh?.driverPhone || editingTrip.driverPhone || '',
        status: editStatus,
        updatedAt: new Date().toISOString(),
      };

      if (selectedVeh?.capacity) {
        updates.capacity = selectedVeh.capacity;
        updates.totalSeats = selectedVeh.capacity;
      }

      await adminBookingService.updateTrip(editingTrip.id, updates);

      const updatedTripObj: Trip = {
        ...editingTrip,
        ...updates,
      };

      setTrips((prev) => prev.map((t) => (t.id === editingTrip.id ? updatedTripObj : t)));

      if (selectedTripManifest?.trip.id === editingTrip.id) {
        setSelectedTripManifest((prev) => (prev ? { ...prev, trip: updatedTripObj } : null));
      }

      setIsEditModalOpen(false);
      setEditingTrip(null);
      setMessage({
        text: `تم تحديث بيانات الرحلة (${editingTrip.tripCode || editingTrip.id}) بنجاح!`,
        type: 'success',
      });

      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('oto_data_sync'));
      }
    } catch (err: any) {
      console.error('Failed to update trip:', err);
      alert('حدث خطأ أثناء تعديل بيانات الرحلة: ' + (err?.message || 'يرجى المحاولة مجدداً'));
    } finally {
      setActionLoading(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  // Delete trip cleanly from Firestore and state immediately
  const handleDeleteTrip = async (trip: Trip) => {
    if (
      !window.confirm(
        `هل أنت متأكد من حذف هذه الرحلة (${trip.tripCode || trip.id}) نهائياً من قاعدة البيانات وجدول الرحلات؟`
      )
    ) {
      return;
    }
    setActionLoading(true);

    // Immediate UI removal
    setTrips((prev) => prev.filter((t) => t.id !== trip.id));
    if (selectedTripManifest?.trip.id === trip.id) {
      setSelectedTripManifest(null);
    }

    try {
      await adminBookingService.deleteTrip(trip.id);
    } catch (err: any) {
      console.warn('Trip delete fallback error:', err);
    } finally {
      setActionLoading(false);
      setMessage({
        text: `تم حذف الرحلة (${trip.tripCode || trip.id}) نهائياً من قاعدة البيانات بنجاح!`,
        type: 'success',
      });
      setTimeout(() => setMessage(null), 4000);
    }
  };

  // Delete all trips cleanly from database and state (Zero out trips)
  const handleDeleteAllTrips = async () => {
    if (
      !window.confirm(
        'هل أنت متأكد من رغبتك في حذف كافة الرحلات نهائياً من قاعدة البيانات وتصفير جدول الرحلات بالكامل؟'
      )
    ) {
      return;
    }

    setActionLoading(true);
    try {
      for (const t of trips) {
        try {
          await adminBookingService.deleteTrip(t.id);
        } catch {}
      }
      setTrips([]);
      setSelectedTripManifest(null);
      setMessage({
        text: 'تم حذف كافة الرحلات بنجاح وتصفير جدول الرحلات تماماً!',
        type: 'success',
      });
    } catch (err: any) {
      console.warn('Trips delete fallback:', err);
      setTrips([]);
      setMessage({
        text: 'تم تصفير جدول الرحلات محلياً بنجاح.',
        type: 'success',
      });
    } finally {
      setActionLoading(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  // Create new trips from interactive calendar selection
  const handleSaveTrip = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedDaysOfMonth.length === 0) {
      alert('يرجى تحديد يوم سفر واحد على الأقل في الشهر من التقويم لجدولة الرحلة.');
      return;
    }

    const uni = universities.find((u) => u.id === newUniId) || universities[0];
    if (!uni) {
      alert('يرجى اختيار الجامعة الوجهة أولاً، وتأكد من تسجيل الجامعات في قسم الجامعات.');
      return;
    }

    setActionLoading(true);

    try {
      const veh = selectedVehicle;
      const tripCapacity = veh?.capacity || 50;
      const nowIso = new Date().toISOString();

      const parts = selectedMonth.split('-');
      const year = Number(parts[0]) || 2026;
      const month = Number(parts[1]) || 10;
      const createdTrips: Trip[] = [];

      for (const day of selectedDaysOfMonth) {
        const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        const id = `trip_${uni.id}_${dateStr.replace(/-/g, '')}_${newDirection.toLowerCase()}_${Date.now().toString().slice(-4)}_${Math.random().toString(36).substring(2, 5)}`;

        const resolvedStation =
          newLaunchStation === 'CUSTOM'
            ? (customStationName.trim() || DEFAULT_LAUNCH_STATION)
            : newLaunchStation;

        const tripOrigin =
          newDirection === 'RETURN'
            ? uni.campus || uni.name
            : resolvedStation;

        const tripDestination =
          newDirection === 'RETURN'
            ? resolvedStation
            : uni.campus || uni.name;

        const trip: Trip = {
          id,
          tripCode: `NGS-${(uni.id || 'UNI').substring(0, 5).toUpperCase()}-${dateStr.replace(/-/g, '').slice(-4)}-${newDirection === 'RETURN' ? 'R' : 'O'}`,
          universityId: uni.id,
          universityName: uni.name,
          governorate: uni.governorate || 'غير محدد',
          direction: newDirection,
          date: dateStr,
          departureTime: newDepTime,
          expectedArrivalTime: newArrTime,
          origin: tripOrigin,
          destination: tripDestination,
          vehicleId: veh?.id || 'veh_custom',
          vehiclePlate: veh?.vehicleNumber || veh?.plateNumber || 'س ق د 1842',
          vehicleNumber: veh?.vehicleNumber || veh?.plateNumber || 'س ق د 1842',
          driverName: veh?.driverName || '',
          driverPhone: veh?.driverPhone || '',
          totalSeats: tripCapacity,
          capacity: tripCapacity,
          availableSeats: tripCapacity,
          availableSeatsCount: tripCapacity,
          price: Number(newPrice) || 150, // سعر رحلة واحدة في يوم واحد للفرد
          currency: 'EGP',
          status: 'OPEN',
          createdAt: nowIso,
          updatedAt: nowIso,
        };

        createdTrips.push(trip);
      }

      // Fast parallel save to Firestore
      await Promise.all(
        createdTrips.map(async (trip) => {
          try {
            await setDoc(doc(db, 'trips', trip.id), trip, { merge: true });
          } catch (writeErr) {
            console.warn('Trip save write warning:', writeErr);
          }
        })
      );

      // Auto-activate university in Firestore so it's guaranteed visible to students
      try {
        await setDoc(
          doc(db, 'universities', uni.id),
          { active: true, isActive: true, updatedAt: nowIso },
          { merge: true }
        );
        universityService.invalidateCache();
      } catch (uErr) {
        console.warn('Could not auto-activate university on trip creation:', uErr);
      }

      // Immediate UI update
      setTrips((prev) => [...createdTrips, ...prev]);
      setMessage({
        text:
          createdTrips.length > 1
            ? `تمت بنجاح جدولة ${createdTrips.length} رحلة لشهر ${getArabicMonthTitle(selectedMonth)} بسعر ${newPrice} ج.م للرحلة (${tripCapacity} مقعداً لكل رحلة بناء على سعة المركبة)!`
            : `تمت جدولة الرحلة ليوم ${createdTrips[0]?.date} بسعر ${newPrice} ج.م وتهيئة ${tripCapacity} مقعداً للحجز بنجاح!`,
        type: 'success',
      });
      setIsCreateModalOpen(false);
    } catch (err: any) {
      console.error('Error saving trip batch:', err);
      alert('حدث خطأ أثناء حفظ الرحلات: ' + (err?.message || 'يرجى المحاولة مجدداً'));
    } finally {
      setActionLoading(false);
      setTimeout(() => setMessage(null), 5000);
    }
  };

  // Inspect Seats and full passenger manifest
  const handleInspectSeats = async (trip: Trip) => {
    setActionLoading(true);
    try {
      const matchedVeh = vehicles.find(
        (v) =>
          v.id === trip.vehicleId ||
          v.vehicleNumber === trip.vehiclePlate ||
          v.plateNumber === trip.vehiclePlate
      );
      const totalSeatsCount = matchedVeh?.capacity || trip.totalSeats || trip.capacity || 50;
      const seats = await tripService.getTripSeats(trip.id, totalSeatsCount);

      // Query bookings for this trip
      let tripBookings: Booking[] = [];
      try {
        const q = query(bookingsCol, where('tripId', '==', trip.id));
        const snap = await getDocs(q);
        if (!snap.empty) {
          tripBookings = snap.docs
            .map((d) => ({ ...d.data(), id: d.id } as Booking))
            .filter((b) => (b.status || b.bookingStatus) !== 'CANCELLED');
        }
      } catch (err) {
        console.warn('Could not query bookings by tripId:', err);
      }

      // Check session storage and local storage dev bookings
      if (typeof window !== 'undefined') {
        const raw = sessionStorage.getItem('nashgo_dev_student_bookings') || localStorage.getItem('nashgo_dev_student_bookings');
        if (raw) {
          try {
            const devList = JSON.parse(raw) as Booking[];
            const matching = devList.filter(
              (b) => b.tripId === trip.id && (b.status || b.bookingStatus) !== 'CANCELLED' && (b.status || b.bookingStatus) !== 'DELETED'
            );
            for (const mb of matching) {
              if (!tripBookings.some((existing) => existing.id === mb.id)) {
                tripBookings.push(mb);
              }
            }
          } catch {}
        }
      }

      // Synthesize passenger manifest for all seats (with collision protection)
      const allocatedBookingIds = new Set<string>();
      const passengers: TripPassengerEntry[] = Array.from({ length: totalSeatsCount }).map((_, idx) => {
        const seatNum = idx + 1;
        const booking = tripBookings.find((b) => Number(b.seatNumber) === seatNum && !allocatedBookingIds.has(b.id));
        const seatObj = seats.find((s) => Number(s.seatNumber) === seatNum);
        const isBookedInSeatObj = seatObj?.status === 'BOOKED' || seatObj?.status === 'HELD';

        if (booking) {
          allocatedBookingIds.add(booking.id);
          const bookingStation =
            (booking as any)?.pickupStation ||
            (booking as any)?.station ||
            (booking as any)?.fromStation ||
            (booking as any)?.launchStation ||
            trip.launchStation ||
            DEFAULT_LAUNCH_STATION;

          const studentName =
            booking.studentSnapshot?.fullName ||
            (booking as any)?.studentName ||
            (booking as any)?.fullName ||
            'طالب مسجل';

          const studentPhone =
            booking.studentSnapshot?.phoneNumber ||
            (booking as any)?.studentPhone ||
            (booking as any)?.phoneNumber ||
            (booking as any)?.phone ||
            '010XXXXXXXX';

          const ticketId =
            booking.ticketId ||
            (booking as any)?.ticketNumber ||
            booking.bookingCode ||
            `TCK-${seatNum}`;

          const bookingCode =
            booking.bookingCode ||
            (booking as any)?.code ||
            `NGS-${seatNum}`;

          return {
            seatNumber: seatNum,
            booking,
            studentName,
            studentPhone,
            bookingCode,
            ticketId,
            paymentStatus: booking.paymentStatus || 'PAID',
            pickupStation: bookingStation,
            attendance: 'PENDING',
          };
        } else if (isBookedInSeatObj) {
          return {
            seatNumber: seatNum,
            studentName: (seatObj as any)?.studentName || `مقعد محجوز (${seatNum})`,
            studentPhone: (seatObj as any)?.studentPhone || (seatObj as any)?.phone || '—',
            bookingCode: (seatObj as any)?.bookingCode || `RSV-${seatNum}`,
            ticketId: (seatObj as any)?.ticketId || (seatObj as any)?.ticketNumber || `TCK-${seatNum}`,
            paymentStatus: 'PAID',
            pickupStation: trip.launchStation || DEFAULT_LAUNCH_STATION,
            attendance: 'PENDING',
          };
        }

        return {
          seatNumber: seatNum,
          pickupStation: trip.launchStation || DEFAULT_LAUNCH_STATION,
          attendance: 'PENDING',
        };
      });

      // Safety: Allocate any remaining unplaced bookings into vacant seats
      const unplacedBookings = tripBookings.filter((b) => !allocatedBookingIds.has(b.id));
      for (const unplaced of unplacedBookings) {
        const vacantIdx = passengers.findIndex((p) => !p.booking && !p.studentName);
        if (vacantIdx !== -1) {
          allocatedBookingIds.add(unplaced.id);
          const seatNum = vacantIdx + 1;
          const bookingStation =
            (unplaced as any)?.pickupStation ||
            (unplaced as any)?.station ||
            trip.launchStation ||
            DEFAULT_LAUNCH_STATION;

          passengers[vacantIdx] = {
            seatNumber: seatNum,
            booking: unplaced,
            studentName: unplaced.studentSnapshot?.fullName || (unplaced as any)?.studentName || 'طالب مسجل',
            studentPhone: unplaced.studentSnapshot?.phoneNumber || (unplaced as any)?.phoneNumber || '—',
            bookingCode: unplaced.bookingCode || `NGS-${seatNum}`,
            ticketId: unplaced.ticketId || `TCK-${seatNum}`,
            paymentStatus: unplaced.paymentStatus || 'AWAITING_TRANSFER',
            pickupStation: bookingStation,
            attendance: 'PENDING',
          };
        }
      }

      setSelectedTripManifest({
        trip,
        seats,
        passengers,
      });
      setSelectedManifestSeat(null);
      setManifestFilter('ALL');
      setManifestSearchTerm('');
    } catch (err) {

      console.warn('Could not load seats for trip:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleAttendance = (seatNumber: number, status: 'BOARDED' | 'NO_SHOW' | 'PENDING') => {
    if (!selectedTripManifest) return;
    setSelectedTripManifest((prev) =>
      prev
        ? {
            ...prev,
            passengers: prev.passengers.map((p) =>
              p.seatNumber === seatNumber ? { ...p, attendance: status } : p
            ),
          }
        : null
    );
  };

  // Mark trip as completed / departed (moves to 'الرحلات التي قامت وسافرت')
  const handleMarkTripCompleted = async (trip: Trip) => {
    if (
      !window.confirm(
        `هل أنت متأكد من إنهاء الرحلة (${trip.tripCode || trip.id}) ونقلها إلى سجل الرحلات التي قامت وسافرت؟`
      )
    ) {
      return;
    }

    const nowIso = new Date().toISOString();
    setTrips((prev) =>
      prev.map((t) => (t.id === trip.id ? { ...t, status: 'COMPLETED', updatedAt: nowIso } : t))
    );
    if (selectedTripManifest?.trip.id === trip.id) {
      setSelectedTripManifest((prev) =>
        prev ? { ...prev, trip: { ...prev.trip, status: 'COMPLETED', updatedAt: nowIso } } : null
      );
    }
    setActionLoading(true);

    try {
      const docRef = doc(db, 'trips', trip.id);
      await updateDoc(docRef, { status: 'COMPLETED', updatedAt: nowIso });
      adminBookingService.invalidateTripsCache();
      setMessage({
        text: `تم إنهاء الرحلة (${trip.tripCode || trip.id}) ونقلها بنجاح إلى سجل الرحلات التي قامت وسافرت!`,
        type: 'success',
      });
    } catch (err: any) {
      console.warn('Trip completion Firestore error, saved locally:', err);
      setMessage({
        text: `تم نقل الرحلة محلياً إلى سجل الرحلات المنتهية.`,
        type: 'success',
      });
    } finally {
      setActionLoading(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  // Restore completed trip back to upcoming trips
  const handleRestoreTripToUpcoming = async (trip: Trip) => {
    if (
      !window.confirm(
        `هل تريد إعادة تفعيل الرحلة (${trip.tripCode || trip.id}) وإعادتها لسجل الرحلات القادمة المتاحة للحجز؟`
      )
    ) {
      return;
    }

    const nowIso = new Date().toISOString();
    setTrips((prev) =>
      prev.map((t) => (t.id === trip.id ? { ...t, status: 'OPEN', updatedAt: nowIso } : t))
    );
    if (selectedTripManifest?.trip.id === trip.id) {
      setSelectedTripManifest((prev) =>
        prev ? { ...prev, trip: { ...prev.trip, status: 'OPEN', updatedAt: nowIso } } : null
      );
    }
    setActionLoading(true);

    try {
      const docRef = doc(db, 'trips', trip.id);
      await updateDoc(docRef, { status: 'OPEN', updatedAt: nowIso });
      adminBookingService.invalidateTripsCache();
      setMessage({
        text: `تمت إعادة الرحلة (${trip.tripCode || trip.id}) بنجاح إلى سجل الرحلات القادمة المتاحة!`,
        type: 'success',
      });
    } catch (err: any) {
      console.warn('Trip restore Firestore error, saved locally:', err);
      setMessage({
        text: `تمت إعادة الرحلة محلياً إلى سجل الرحلات القادمة.`,
        type: 'success',
      });
    } finally {
      setActionLoading(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  // Toggle Trip Status immediately
  const handleToggleStatus = async (trip: Trip) => {
    const nextStatus: TripStatus = trip.status === 'OPEN' ? 'FULL' : 'OPEN';
    // Immediate optimistic update
    setTrips((prev) => prev.map((t) => (t.id === trip.id ? { ...t, status: nextStatus } : t)));
    setActionLoading(true);

    try {
      const docRef = doc(db, 'trips', trip.id);
      await updateDoc(docRef, { status: nextStatus, updatedAt: new Date().toISOString() });
      setMessage({
        text: `تم تغيير حالة الرحلة ${trip.tripCode || trip.id} إلى ${nextStatus === 'OPEN' ? 'مفتوحة للحجز' : 'مكتملة'}.`,
        type: 'success',
      });
    } catch (err: any) {
      console.warn('Trip status update local fallback:', err);
      setMessage({ text: `تم تحديث حالة الرحلة محلياً.`, type: 'success' });
    } finally {
      setActionLoading(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  // Reconcile and audit all trip seat counters with authoritative bookings
  const handleReconcileSeats = async () => {
    setActionLoading(true);
    try {
      const res = await adminBookingService.reconcileAllTripSeats();
      setMessage({
        text:
          res.correctedTrips > 0
            ? `تم بنجاح تدقيق وتصحيح عدادات المقاعد لـ ${res.correctedTrips} رحلة متأثرة ومطابقتها مع الحجوزات الفعلية!`
            : `كافة عدادات المقاعد في الرحلات (${res.scannedTrips} رحلة) مطابقة بنسبة 100% لسجلات الحجز الفعلي ولا يوجد أي خلل.`,
        type: 'success',
      });
      await loadData(true);
    } catch (err: any) {
      setMessage({
        text: `تعذر إتمام تدقيق المقاعد: ${err?.message || 'خطأ غير متوقع'}`,
        type: 'error',
      });
    } finally {
      setActionLoading(false);
      setTimeout(() => setMessage(null), 6000);
    }
  };

  return (
    <div className="admin-page-container" dir="rtl">
      {/* Header */}
      <div className="admin-page-header">
        <div className="admin-header-title-box">
          <h2>
            <Bus size={24} style={{ color: '#E5232A' }} />
            <span>جدول الرحلات والمواعيد اليومية</span>
          </h2>
          <p>إدارة مواعيد السفر الصباحية والمسائية، متابعة إشغال المقاعد (50 راكباً بالحافلة)، وجدولة رحلات فردية وخطوط ثابتة شهرياً.</p>
        </div>

        <div className="admin-header-actions-row">
          <button
            type="button"
            className="btn-admin-primary"
            style={{
              background: 'linear-gradient(135deg, #4338CA 0%, #312E81 100%)',
              borderColor: '#3730A3',
              boxShadow: '0 2px 8px rgba(67, 56, 202, 0.25)',
            }}
            onClick={() => setIsBulkModalOpen(true)}
            title="تعديل الأسعار، المواعيد، الأسطول، أو حذف الرحلات لخط سير كامل مع المعاينة وتوليد الخطة أولاً"
          >
            <Sparkles size={18} />
            <span>تعديل كلي لخط السير (مع الخطة)</span>
          </button>
          <button type="button" className="btn-admin-primary" onClick={handleOpenCreate}>
            <Plus size={18} />
            <span>جدولة رحلة جديدة</span>
          </button>
          {trips.length > 0 && (
            <button
              type="button"
              className="btn-admin-danger"
              style={{ background: '#FEE2E2', color: '#DC2626', borderColor: '#FCA5A5' }}
              onClick={handleDeleteAllTrips}
              disabled={actionLoading}
              title="حذف وتصفير كافة الرحلات نهائياً"
            >
              <Trash2 size={16} />
              <span>تصفير وحذف كافة الرحلات</span>
            </button>
          )}
          <button
            type="button"
            className="btn-admin-outline"
            style={{ borderColor: '#6366F1', color: '#4F46E5', background: '#EEF2FF' }}
            onClick={handleReconcileSeats}
            disabled={actionLoading || isLoading}
            title="تدقيق وتصحيح مطابقة عدادات المقاعد مع الحجوزات الفعلية"
          >
            <Wrench size={16} className={actionLoading ? 'animate-spin' : ''} />
            <span>تدقيق وتصحيح المقاعد</span>
          </button>
          <button type="button" className="btn-admin-outline" onClick={() => loadData(true)} disabled={isLoading}>
            <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
            <span>تحديث</span>
          </button>
        </div>
      </div>

      {/* Message alert */}
      {message && (
        <div className={`p-3 rounded-xl border flex items-center gap-2 ${message.type === 'success' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-red-50 text-red-800 border-red-200'}`}>
          <CheckCircle2 size={18} />
          <span>{message.text}</span>
        </div>
      )}

      {/* Stats */}
      <div className="admin-stats-strip">
        <div className="admin-stat-card">
          <div className="stat-icon blue">
            <Bus size={22} />
          </div>
          <div className="stat-meta">
            <span className="stat-val font-mono">{stats.total}</span>
            <span className="stat-lbl">إجمالي كافة الرحلات</span>
          </div>
        </div>

        <div
          className="admin-stat-card cursor-pointer hover:border-emerald-300 transition-all"
          onClick={() => setTripTab('UPCOMING')}
          title="عرض سجل الرحلات القادمة"
          style={tripTab === 'UPCOMING' ? { borderColor: '#10B981', background: '#F0FDF4' } : {}}
        >
          <div className="stat-icon green">
            <Calendar size={22} />
          </div>
          <div className="stat-meta">
            <span className="stat-val font-mono text-emerald-700">{stats.upcoming}</span>
            <span className="stat-lbl text-emerald-800 font-bold">رحلات قادمة مجدولة</span>
          </div>
        </div>

        <div
          className="admin-stat-card cursor-pointer hover:border-purple-300 transition-all"
          onClick={() => setTripTab('COMPLETED')}
          title="عرض سجل الرحلات التي قامت وسافرت"
          style={tripTab === 'COMPLETED' ? { borderColor: '#059669', background: '#ECFDF5' } : {}}
        >
          <div className="stat-icon" style={{ background: '#D1FAE5', color: '#047857' }}>
            <CheckCircle2 size={22} />
          </div>
          <div className="stat-meta">
            <span className="stat-val font-mono text-emerald-800">{stats.completed}</span>
            <span className="stat-lbl text-emerald-900 font-bold">رحلات قامت وسافرت (منتهية)</span>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="stat-icon amber">
            <Calendar size={22} />
          </div>
          <div className="stat-meta">
            <span className="stat-val font-mono">{stats.todayTrips}</span>
            <span className="stat-lbl">رحلات اليوم</span>
          </div>
        </div>
      </div>

      {/* Registers / Tabs: Upcoming vs Departed & Completed Trips */}
      <div className="admin-tabs-bar" style={{ marginTop: '0.25rem', marginBottom: '0.25rem' }}>
        <button
          type="button"
          className={`admin-tab-btn ${tripTab === 'UPCOMING' ? 'active' : ''}`}
          onClick={() => setTripTab('UPCOMING')}
        >
          <Calendar size={16} />
          <span>سجل الرحلات القادمة</span>
          <span className="tab-counter-pill">{tripCounts.upcoming}</span>
        </button>

        <button
          type="button"
          className={`admin-tab-btn ${tripTab === 'COMPLETED' ? 'active' : ''}`}
          onClick={() => setTripTab('COMPLETED')}
          style={tripTab === 'COMPLETED' ? { background: '#059669', color: '#ffffff' } : {}}
        >
          <CheckCircle2 size={16} />
          <span>سجل الرحلات التي قامت وسافرت (المنتهية)</span>
          <span className={`tab-counter-pill ${tripTab === 'COMPLETED' ? '' : 'green'}`}>
            {tripCounts.completed}
          </span>
        </button>
      </div>

      {/* Toolbar */}
      <div className="admin-toolbar-card">
        <div className="admin-search-wrapper">
          <Search size={18} />
          <input
            type="text"
            className="admin-search-input"
            placeholder={
              tripTab === 'UPCOMING'
                ? 'البحث في الرحلات القادمة بكود الرحلة، الجامعة، التاريخ، التوقيت، السائق...'
                : 'البحث في سجل الرحلات التي قامت وسافرت بكود الرحلة، الجامعة، التاريخ...'
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="admin-filter-group">
          {/* Uni Filter */}
          <select
            className="admin-filter-select"
            value={selectedUniFilter}
            onChange={(e) => setSelectedUniFilter(e.target.value)}
          >
            <option value="ALL">جميع الجامعات</option>
            {universities.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>

          {/* Direction Filter */}
          <select
            className="admin-filter-select"
            value={selectedDirFilter}
            onChange={(e) => setSelectedDirFilter(e.target.value)}
          >
            <option value="ALL">كافة الاتجاهات</option>
            <option value="OUTBOUND">ذهاب (الإسكندرية ◄ الجامعة)</option>
            <option value="RETURN">عودة (الجامعة ◄ الإسكندرية)</option>
          </select>

          {/* Launch Station Filter */}
          <select
            className="admin-filter-select"
            value={selectedStationFilter}
            onChange={(e) => setSelectedStationFilter(e.target.value)}
          >
            <option value="ALL">كافة محطات الانطلاق</option>
            <option value="الموقف الجديد بمحرم بيك">الموقف الجديد بمحرم بيك</option>
            <option value="الكيلو 21">الكيلو 21</option>
          </select>

          {/* Status Filter */}
          <select
            className="admin-filter-select"
            value={selectedStatusFilter}
            onChange={(e) => setSelectedStatusFilter(e.target.value)}
          >
            <option value="ALL">كافة الحالات</option>
            <option value="OPEN">مفتوحة (OPEN)</option>
            <option value="FULL">مكتملة (FULL)</option>
          </select>
        </div>
      </div>

      {/* Trips Table */}
      <div className="admin-table-container">
        {isLoading ? (
          <div className="table-empty-state">
            <RefreshCw size={32} className="animate-spin text-[#E5232A]" />
            <p>جاري تحميل جدول الرحلات...</p>
          </div>
        ) : filteredTrips.length === 0 ? (
          <div className="table-empty-state">
            <Bus size={40} />
            <p>
              {tripTab === 'UPCOMING'
                ? 'لا توجد رحلات قادمة تطابق شروط الفلترة المحددة.'
                : 'لا توجد رحلات في سجل الرحلات التي قامت وسافرت تطابق البحث.'}
            </p>
          </div>
        ) : (
          <table className="admin-data-table">
            <thead>
              <tr>
                <th>كود الرحلة</th>
                <th>الجامعة والمسار</th>
                <th>الاتجاه</th>
                <th>تاريخ السفر</th>
                <th>وقت الإقلاع والوصول</th>
                <th>المقاعد المتاحة</th>
                <th>السعر</th>
                <th>الحالة</th>
                <th>إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {visibleTrips.map((t, idx) => {
                const totalSeats = t.totalSeats || t.capacity || 50;
                const realBookedCount = tripBookingsCountMap[t.id];
                const booked = typeof realBookedCount === 'number'
                  ? realBookedCount
                  : Math.max(0, totalSeats - Math.min(totalSeats, Math.max(0, typeof t.availableSeats === 'number' ? t.availableSeats : totalSeats)));
                const avail = Math.max(0, totalSeats - booked);
                const percent = Math.min(100, Math.max(0, Math.round((booked / totalSeats) * 100)));

                const uTheme = getUniversityTheme(t.universityName);
                const prevTrip = idx > 0 ? visibleTrips[idx - 1] : null;
                const isNewDate = !prevTrip || prevTrip.date !== t.date;
                const isReturnSwitch = !isNewDate && prevTrip?.direction !== 'RETURN' && t.direction === 'RETURN';

                return (
                  <React.Fragment key={t.id}>
                    {/* Date Section Header */}
                    {isNewDate && (
                      <tr key={`date-hdr-${t.date}-${idx}`} style={{ background: '#F8FAFC' }}>
                        <td colSpan={9} style={{ padding: '9px 16px', borderTop: '2px solid #E2E8F0', borderBottom: '1px solid #CBD5E1' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 800, color: '#0F172A', fontSize: '13px' }}>
                              <Calendar size={16} style={{ color: '#E5232A' }} />
                              <span>📅 جدول رحلات: {formatArabicFullDate(t.date)} ({t.date})</span>
                            </div>
                            <span style={{ fontSize: '11px', fontWeight: 700, color: '#475569', background: '#FFFFFF', padding: '3px 10px', borderRadius: '12px', border: '1px solid #E2E8F0' }}>
                              رحلات الذهاب أولاً بالترتيب المعتمد للجامعات ثم رحلات العودة
                            </span>
                          </div>
                        </td>
                      </tr>
                    )}

                    {/* Return Section Switch Header */}
                    {isReturnSwitch && (
                      <tr key={`ret-hdr-${t.date}-${idx}`} style={{ background: '#FEF3C7' }}>
                        <td colSpan={9} style={{ padding: '7px 16px', borderTop: '1px dashed #F59E0B', borderBottom: '1px dashed #F59E0B' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 800, color: '#92400E', fontSize: '12px' }}>
                            <Repeat size={14} style={{ color: '#D97706' }} />
                            <span>🔁 رحلات العودة إلى الإسكندرية لنفس اليوم ({t.date}) — مرتبة حسب الجامعات</span>
                          </div>
                        </td>
                      </tr>
                    )}

                    <tr
                      style={{
                        borderRight: `6px solid ${uTheme.accentBar}`,
                        transition: 'background-color 0.15s ease',
                      }}
                    >
                      <td>
                        <strong className="font-mono text-[#E5232A]">{t.tripCode || t.id.slice(0, 12)}</strong>
                      </td>
                      <td>
                        <div>
                          <div
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '4px 10px',
                              borderRadius: '8px',
                              backgroundColor: uTheme.badgeBg,
                              border: `1.5px solid ${uTheme.badgeBorder}`,
                              color: uTheme.badgeText,
                              fontWeight: 800,
                              fontSize: '12.5px',
                              marginBottom: '4px',
                            }}
                          >
                            <span
                              style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: uTheme.dotColor,
                                display: 'inline-block',
                                boxShadow: `0 0 0 2px ${uTheme.badgeBorder}`,
                              }}
                            />
                            <span>{t.universityName}</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-0.5">
                            <span className="font-bold text-rose-700 bg-rose-50 px-1.5 py-0.5 rounded border border-rose-200">
                              {t.origin?.includes('21') ? 'الكيلو 21' : (t.origin?.includes('محرم') ? 'محرم بيك' : t.origin || 'الموقف الجديد')}
                            </span>
                            <span>{t.vehiclePlate || 'مركبة معتمدة'}</span>
                            {t.driverName && (
                              <span className="text-blue-600 font-medium">| السائق: {t.driverName}</span>
                            )}
                          </div>
                        </div>
                      </td>
                    <td>
                      <span className={`dir-badge ${t.direction === 'RETURN' ? 'return' : 'outbound'}`}>
                        {t.direction === 'RETURN' ? (
                          <>
                            <Repeat size={12} />
                            <span>عودة للإسكندرية</span>
                          </>
                        ) : (
                          <>
                            <ArrowRight size={12} />
                            <span>ذهاب للجامعة</span>
                          </>
                        )}
                      </span>
                    </td>
                    <td>
                      <span className="font-mono font-bold text-slate-800">{t.date}</span>
                    </td>
                    <td>
                      <div>
                        <span className="font-mono font-bold block">{t.departureTime}</span>
                        <span className="text-xs text-slate-400">وصول متوقع {t.expectedArrivalTime || '—'}</span>
                      </div>
                    </td>
                    <td>
                      <div className="w-32">
                        <div className="flex justify-between items-center text-xs mb-1">
                          <span className="font-bold text-slate-700">{avail} شاغر</span>
                          <span className="text-slate-500 font-mono text-[11px] bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200" title="المقاعد المحجوزة / إجمالي المقاعد">
                            {booked} / {totalSeats}
                          </span>
                        </div>
                        <div className="w-full bg-slate-200 h-1.5 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${percent >= 100 ? 'bg-red-500' : percent >= 70 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                            style={{ width: `${percent}%` }}
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <strong className="font-mono">{t.price} ج.م</strong>
                    </td>
                    <td>
                      {t.status === 'COMPLETED' || t.status === 'DEPARTED' ? (
                        <span
                          className="status-pill"
                          style={{
                            background: '#ECFDF5',
                            color: '#065F46',
                            border: '1px solid #A7F3D0',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontWeight: 700,
                          }}
                        >
                          <CheckCircle2 size={12} />
                          <span>قامت وسافرت</span>
                        </span>
                      ) : (
                        <span className={`status-pill ${t.status === 'OPEN' ? 'confirmed' : 'cancelled'}`}>
                          {t.status === 'OPEN' ? 'مفتوحة' : 'مكتملة'}
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <button
                          type="button"
                          className="btn-admin-outline"
                          title="عرض مقاعد وركاب الرحلة"
                          onClick={() => handleInspectSeats(t)}
                        >
                          <Eye size={14} />
                          <span>المقاعد</span>
                        </button>

                        <button
                          type="button"
                          className="btn-admin-outline"
                          style={{
                            borderColor: '#3B82F6',
                            color: '#1D4ED8',
                            background: '#EFF6FF',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontWeight: 700,
                          }}
                          title="تعديل بيانات الرحلة (سعر التذكرة، الموعد، السائق، المحطة)"
                          onClick={() => handleOpenEdit(t)}
                        >
                          <Edit size={13} />
                          <span>تعديل</span>
                        </button>

                        {/* زر نقل الرحلة إلى سجل الرحلات التي قامت وسافرت أو العكس */}
                        {t.status !== 'COMPLETED' && t.status !== 'DEPARTED' ? (
                          <button
                            type="button"
                            className="btn-admin-primary"
                            style={{
                              background: '#059669',
                              borderColor: '#059669',
                              color: '#ffffff',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '5px 9px',
                              fontSize: '0.75rem',
                              fontWeight: 700,
                            }}
                            title="نقل هذه الرحلة إلى سجل الرحلات التي قامت وسافرت"
                            onClick={() => handleMarkTripCompleted(t)}
                            disabled={actionLoading}
                          >
                            <CheckCircle2 size={13} />
                            <span>الرحلات المنتهية</span>
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn-admin-outline"
                            style={{
                              borderColor: '#2563EB',
                              color: '#1D4ED8',
                              background: '#EFF6FF',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '5px 9px',
                              fontSize: '0.75rem',
                              fontWeight: 700,
                            }}
                            title="إعادة الرحلة إلى سجل الرحلات القادمة"
                            onClick={() => handleRestoreTripToUpcoming(t)}
                            disabled={actionLoading}
                          >
                            <RotateCcw size={13} />
                            <span>إعادة للقادمة</span>
                          </button>
                        )}

                        {t.status !== 'COMPLETED' && t.status !== 'DEPARTED' && (
                          <button
                            type="button"
                            className={t.status === 'OPEN' ? 'btn-admin-danger' : 'btn-admin-success'}
                            onClick={() => handleToggleStatus(t)}
                            disabled={actionLoading}
                            title={t.status === 'OPEN' ? 'إغلاق الحجز' : 'إعادة فتح الحجز'}
                          >
                            {t.status === 'OPEN' ? 'إغلاق' : 'فتح'}
                          </button>
                        )}

                        <button
                          type="button"
                          className="btn-admin-danger"
                          style={{ background: '#FEE2E2', color: '#DC2626', borderColor: '#FCA5A5' }}
                          title="حذف الرحلة نهائياً"
                          onClick={() => handleDeleteTrip(t)}
                          disabled={actionLoading}
                        >
                          <Trash2 size={14} />
                          <span>حذف</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Load More Footer (15 initial + 10 more on button click) */}
        {filteredTrips.length > 0 && (
          <div style={{ padding: '0 12px 14px' }}>
            <AdminLoadMore
              currentCount={Math.min(visibleCount, filteredTrips.length)}
              totalCount={filteredTrips.length}
              itemName="رحلة"
              onLoadMore={() => setVisibleCount((prev) => prev + 10)}
              step={10}
              onShowAll={() => setVisibleCount(filteredTrips.length)}
            />
          </div>
        )}
      </div>

      {/* Create Trip Modal */}
      {isCreateModalOpen && (
        <div className="admin-modal-overlay" onClick={() => setIsCreateModalOpen(false)}>
          <div
            className="admin-modal-card"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '1220px',
              width: '96vw',
              maxHeight: '92vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              borderRadius: '20px',
            }}
          >
            <form onSubmit={handleSaveTrip} style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
              <div className="admin-modal-header" style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #E2E8F0', flexShrink: 0 }}>
                <div className="flex items-center gap-2">
                  <Bus size={22} className="text-[#E5232A]" />
                  <div>
                    <h3 className="text-base font-bold text-slate-800">جدولة رحلة جامعية جديدة</h3>
                    <span className="text-xs text-slate-500">حافلات وميكروباصات سياحية مجهزة بسعة {selectedVehicleCapacity} راكباً</span>
                  </div>
                </div>
                <button
                  type="button"
                  className="admin-modal-close-btn"
                  onClick={() => setIsCreateModalOpen(false)}
                >
                  <X size={20} />
                </button>
              </div>

              <div className="admin-modal-body" style={{ flex: 1, overflowY: 'auto', padding: '1.25rem 1.5rem' }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(430px, 1fr))',
                    gap: '1.5rem',
                    alignItems: 'start',
                  }}
                >
                  {/* RIGHT COLUMN (RTL): Route, Highlighted Price, Vehicle, Times */}
                  <div className="space-y-4">
                    {/* 1. University & Direction */}
                    <div className="form-grid-2">
                      <div className="admin-form-group">
                        <label className="font-bold text-slate-800 text-xs mb-1 block">الجامعة الوجهة *</label>
                        <select
                          value={newUniId}
                          onChange={(e) => setNewUniId(e.target.value)}
                          required
                        >
                          {universities.length === 0 ? (
                            <option value="" disabled>لا توجد جامعات مسجلة في قاعدة البيانات</option>
                          ) : (
                            universities.map((u) => (
                              <option key={u.id} value={u.id}>
                                {u.name} ({u.governorate})
                              </option>
                            ))
                          )}
                        </select>
                      </div>

                      <div className="admin-form-group">
                        <label className="font-bold text-slate-800 text-xs mb-1 block">اتجاه الرحلة *</label>
                        <select
                          value={newDirection}
                          onChange={(e) => setNewDirection(e.target.value as TripDirection)}
                          required
                        >
                          <option value="OUTBOUND">ذهاب (الإسكندرية ◄ الجامعة)</option>
                          <option value="RETURN">عودة (الجامعة ◄ الإسكندرية)</option>
                        </select>
                      </div>
                    </div>

                    {/* 2. Launch Station in Alexandria */}
                    <div className="admin-form-group">
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs font-bold text-slate-800 flex items-center gap-1">
                          <MapPin size={14} className="text-[#E5232A]" />
                          <span>محطة الانطلاق بالإسكندرية (خط السير) *</span>
                        </label>
                        <span className="text-[11px] text-slate-500">
                          الموقف الجديد بمحرم بيك أو الكيلو 21
                        </span>
                      </div>
                      <select
                        value={newLaunchStation}
                        onChange={(e) => setNewLaunchStation(e.target.value)}
                        required
                      >
                        <option value="الموقف الجديد بمحرم بيك">الموقف الجديد بمحرم بيك (المحطة المركزية)</option>
                        <option value="الكيلو 21">الكيلو 21 (محطة غرب الإسكندرية والعجمي)</option>
                        <option value="CUSTOM">✏️ تخصيص / تعديل يدوي من قبل الإدارة فقط</option>
                      </select>
                      {newLaunchStation === 'CUSTOM' && (
                        <input
                          type="text"
                          className="mt-2 text-xs font-bold"
                          placeholder="اكتب محطة الانطلاق المخصصة من قبل الإدارة..."
                          value={customStationName}
                          onChange={(e) => setCustomStationName(e.target.value)}
                          required
                        />
                      )}
                    </div>

                    {/* 3. PROMINENT GLOWING TICKET PRICE BOX (Requested by user: "بالاخص سعر التذكره") */}
                    <div
                      style={{
                        background: 'linear-gradient(135deg, #FEFCE8 0%, #FEF3C7 100%)',
                        border: '2px solid #F59E0B',
                        borderRadius: '16px',
                        padding: '1.1rem 1.25rem',
                        boxShadow: '0 4px 14px rgba(245, 158, 11, 0.16)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '1rem',
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <span style={{ fontSize: '20px' }}>🎟️</span>
                          <label style={{ fontWeight: 800, fontSize: '15px', color: '#92400E', margin: 0 }}>
                            سعر تذكرة الرحلة للفرد (ج.م) *
                          </label>
                        </div>
                        <p style={{ margin: 0, fontSize: '12px', color: '#B45309', fontWeight: 600 }}>
                          يظهر السعر مباشرة للطلاب عند حجز المقاعد ويُحسب تلقائياً في الفاتورة
                        </p>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                        <input
                          type="number"
                          min="10"
                          max="2000"
                          required
                          value={newPrice}
                          onChange={(e) => setNewPrice(Number(e.target.value))}
                          style={{
                            width: '120px',
                            height: '48px',
                            fontSize: '22px',
                            fontWeight: 900,
                            textAlign: 'center',
                            color: '#78350F',
                            backgroundColor: '#FFFFFF',
                            border: '2.5px solid #D97706',
                            borderRadius: '12px',
                            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.06)',
                          }}
                        />
                        <span style={{ fontWeight: 800, color: '#92400E', fontSize: '15px' }}>ج.م / فرد</span>
                      </div>
                    </div>

                    {/* 4. Vehicle Selection & Driver Info */}
                    <div className="admin-form-group">
                      <label className="text-xs font-bold text-slate-800 mb-1 block">
                        المركبة المخصصة (سعة الحجز الفعلي: {selectedVehicleCapacity} مقعداً) *
                      </label>
                      <select
                        value={newVehicleId}
                        onChange={(e) => setNewVehicleId(e.target.value)}
                        required
                      >
                        {(vehicles.length > 0 ? vehicles : SEED_VEHICLES).map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.model || 'مركبة'} — {v.vehicleNumber} (سعة: {v.capacity || 50} مقعداً){v.driverName ? ` — كابتن: ${v.driverName}` : ''}
                          </option>
                        ))}
                      </select>
                      {selectedVehicle && (
                        <div className="mt-2 text-xs bg-slate-50 border border-slate-200 p-2.5 rounded-xl flex items-center justify-between text-slate-700">
                          <span>سعة المقاعد: <strong className="text-rose-600 font-mono font-bold">{selectedVehicleCapacity} مقعداً</strong></span>
                          <span>السائق: <strong className="text-slate-900">{selectedVehicle.driverName || 'لم يُحدد'}</strong> {selectedVehicle.driverPhone ? `(${selectedVehicle.driverPhone})` : ''}</span>
                        </div>
                      )}
                    </div>

                    {/* 5. Departure & Expected Arrival Times */}
                    <div className="form-grid-2">
                      <div className="admin-form-group">
                        <label className="text-xs font-bold text-slate-800 mb-1 block">وقت التحرك / الإقلاع *</label>
                        <input
                          type="time"
                          required
                          value={newDepTime}
                          onChange={(e) => setNewDepTime(e.target.value)}
                        />
                      </div>

                      <div className="admin-form-group">
                        <label className="text-xs font-bold text-slate-800 mb-1 block">وقت الوصول المتوقع</label>
                        <input
                          type="time"
                          value={newArrTime}
                          onChange={(e) => setNewArrTime(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* LEFT COLUMN (RTL): Interactive Calendar & Dates Selector */}
                  <div className="admin-form-group" style={{ margin: 0 }}>
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <label className="flex items-center gap-1.5 font-bold text-slate-800 text-sm">
                        <Calendar size={16} className="text-[#E5232A]" />
                        <span>تقويم وتحديد أيام السفر في الشهر *</span>
                      </label>
                      <span className="text-xs bg-rose-50 text-[#E5232A] px-2.5 py-1 rounded-full font-bold border border-rose-200">
                        تم تحديد {selectedDaysOfMonth.length} أيام للسفر
                      </span>
                    </div>

                    <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3.5 space-y-3">
                      {/* Month Picker & Quick Action Tools */}
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2">
                          <label className="text-xs font-bold text-slate-600">الشهر:</label>
                          <input
                            type="month"
                            value={selectedMonth}
                            onChange={(e) => {
                              if (e.target.value) {
                                setSelectedMonth(e.target.value);
                                const [y, m] = e.target.value.split('-').map(Number);
                                const maxD = new Date(y, m, 0).getDate();
                                setSelectedDaysOfMonth((prev) => prev.filter((d) => d <= maxD));
                              }
                            }}
                            className="text-xs font-bold bg-white border border-slate-300 rounded-lg px-2.5 py-1.5"
                            style={{ width: 'auto' }}
                          />
                          <span className="text-xs font-bold text-slate-700">
                            ({getArabicMonthTitle(selectedMonth)})
                          </span>
                        </div>

                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={selectAllStudyDays}
                            className="cal-quick-btn"
                          >
                            أيام الدراسة (السبت - الخميس)
                          </button>
                          <button
                            type="button"
                            onClick={() => setSelectedDaysOfMonth([])}
                            className="cal-quick-btn danger"
                          >
                            تفريغ
                          </button>
                        </div>
                      </div>

                      {/* Quick Weekday Chips */}
                      <div>
                        <div className="text-[11px] font-bold text-slate-500 mb-1.5">
                          تحديد سريع لكافة أيام أسبوع معينة في هذا الشهر:
                        </div>
                        <div className="cal-weekdays-row">
                          {WEEKDAYS_CONFIG.map((w) => {
                            const parts = selectedMonth.split('-');
                            const y = Number(parts[0]) || 2026;
                            const m = Number(parts[1]) || 10;
                            const maxD = new Date(y, m, 0).getDate();
                            let countInMonth = 0;
                            let countSelected = 0;
                            for (let d = 1; d <= maxD; d++) {
                              if (new Date(y, m - 1, d).getDay() === w.value) {
                                countInMonth++;
                                if (selectedDaysOfMonth.includes(d)) countSelected++;
                              }
                            }
                            const isAllSelected = countInMonth > 0 && countSelected === countInMonth;
                            const isPartial = countSelected > 0 && !isAllSelected;

                            return (
                              <button
                                key={w.value}
                                type="button"
                                onClick={() => toggleAllMatchingWeekday(w.value)}
                                className={`cal-weekday-chip ${
                                  isAllSelected
                                    ? 'all-selected'
                                    : isPartial
                                    ? 'part-selected'
                                    : ''
                                }`}
                              >
                                {w.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* 7-Column Calendar Grid */}
                      {(() => {
                        const parts = selectedMonth.split('-');
                        const year = Number(parts[0]) || 2026;
                        const month = Number(parts[1]) || 10;
                        const daysInMonth = new Date(year, month, 0).getDate();
                        const firstDay = new Date(year, month - 1, 1).getDay();
                        const startOffset = (firstDay + 1) % 7;

                        return (
                          <div className="cal-grid-card">
                            <div className="cal-header-row">
                              <div className="cal-header-cell">السبت</div>
                              <div className="cal-header-cell">الأحد</div>
                              <div className="cal-header-cell">الإثنين</div>
                              <div className="cal-header-cell">الثلاثاء</div>
                              <div className="cal-header-cell">الأربعاء</div>
                              <div className="cal-header-cell">الخميس</div>
                              <div className="cal-header-cell" style={{ color: '#ef4444' }}>الجمعة</div>
                            </div>

                            <div className="cal-days-grid">
                              {Array.from({ length: startOffset }).map((_, i) => (
                                <div key={`empty-${i}`} className="cal-day-empty" />
                              ))}
                              {Array.from({ length: daysInMonth }).map((_, i) => {
                                const dayNum = i + 1;
                                const isSelected = selectedDaysOfMonth.includes(dayNum);
                                return (
                                  <button
                                    key={dayNum}
                                    type="button"
                                    onClick={() => toggleDayOfMonth(dayNum)}
                                    className={`cal-day-cell ${isSelected ? 'is-selected' : ''}`}
                                  >
                                    {dayNum}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Selected Days Summary */}
                      {selectedDaysOfMonth.length > 0 ? (
                        <div className="text-xs">
                          <span className="font-bold text-slate-700 block mb-1">
                            الأيام المحددة في شهر {getArabicMonthTitle(selectedMonth)} ({selectedDaysOfMonth.length} يوماً):
                          </span>
                          <div className="cal-summary-pills">
                            {selectedDaysOfMonth.map((d) => (
                              <span
                                key={d}
                                onClick={() => toggleDayOfMonth(d)}
                                className="cal-pill-tag"
                                title="انقر لإلغاء هذا اليوم"
                              >
                                <span>{d}</span>
                                <X size={10} />
                              </span>
                            ))}
                          </div>
                          <div className="mt-2 text-xs text-emerald-800 bg-emerald-50 p-2 rounded-lg border border-emerald-200 flex items-center gap-1.5">
                            <Sparkles size={14} className="text-emerald-600 flex-shrink-0" />
                            <span>
                              سيتم إنشاء وتجهيز <strong>{selectedDaysOfMonth.length} رحلات مستقلة</strong> ({selectedVehicleCapacity} مقعداً لكل رحلة) بسعر <strong>{newPrice} ج.م</strong> للرحلة.
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="text-xs text-amber-700 bg-amber-50 p-2 rounded-lg border border-amber-200">
                          تنبيه: انقر على الأيام في التقويم أعلاه لتحديد مواعيد الرحلات.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="admin-modal-footer">
                <button
                  type="button"
                  className="btn-admin-outline"
                  onClick={() => setIsCreateModalOpen(false)}
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  className="btn-admin-primary"
                  disabled={actionLoading || selectedDaysOfMonth.length === 0}
                >
                  <CheckCircle2 size={16} />
                  <span>
                    {selectedDaysOfMonth.length > 1
                      ? `تأكيد وجدولة ${selectedDaysOfMonth.length} رحلات في الشهر (${selectedVehicleCapacity} مقعداً لكل رحلة)`
                      : selectedDaysOfMonth.length === 1
                      ? `تأكيد وجدولة الرحلة ليوم ${selectedDaysOfMonth[0]} (${selectedVehicleCapacity} مقعداً)`
                      : 'يرجى تحديد أيام السفر'}
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Trip Modal */}
      {isEditModalOpen && editingTrip && (
        <div className="admin-modal-overlay" onClick={() => setIsEditModalOpen(false)}>
          <div className="admin-modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '660px', width: '95vw' }}>
            <form onSubmit={handleSaveEditTrip}>
              <div className="admin-modal-header">
                <div className="flex items-center gap-2">
                  <div
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '8px',
                      background: '#EFF6FF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#1D4ED8',
                    }}
                  >
                    <Edit size={20} />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-slate-800">
                      تعديل بيانات الرحلة والمواعيد
                    </h3>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="font-mono text-xs font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                        كود: {editingTrip.tripCode || editingTrip.id}
                      </span>
                      <span className={`dir-badge ${editingTrip.direction === 'RETURN' ? 'return' : 'outbound'}`} style={{ fontSize: '11px', padding: '1px 6px' }}>
                        {editingTrip.direction === 'RETURN' ? 'عودة للإسكندرية' : 'ذهاب للجامعة'}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="admin-modal-close-btn"
                  onClick={() => setIsEditModalOpen(false)}
                >
                  <X size={18} />
                </button>
              </div>

              <div className="admin-modal-body" style={{ maxHeight: 'calc(85vh - 140px)', overflowY: 'auto' }}>
                {/* University and Current Stats Banner */}
                <div
                  style={{
                    background: '#F8FAFC',
                    border: '1px solid #E2E8F0',
                    borderRadius: '10px',
                    padding: '10px 14px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1rem',
                  }}
                >
                  <div>
                    <span style={{ fontSize: '0.72rem', color: '#64748B', display: 'block' }}>الجامعة المستهدفة</span>
                    <strong style={{ fontSize: '0.92rem', color: '#0F172A' }}>{editingTrip.universityName}</strong>
                  </div>
                  <div style={{ textAlign: 'left' }}>
                    <span style={{ fontSize: '0.72rem', color: '#64748B', display: 'block' }}>المقاعد المحجوزة</span>
                    <span className="font-mono font-bold text-sm text-slate-800">
                      {editingTrip.totalSeats - editingTrip.availableSeats} / {editingTrip.totalSeats} راكب
                    </span>
                  </div>
                </div>

                {/* Section 1: Pricing & Scheduling Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '1rem' }}>
                  {/* Ticket Price */}
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: '4px' }}>
                      سعر التذكرة (ج.م) *
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="number"
                        min="1"
                        required
                        value={editPrice}
                        onChange={(e) => setEditPrice(e.target.value === '' ? '' : Number(e.target.value))}
                        style={{
                          width: '100%',
                          padding: '8px 12px 8px 38px',
                          borderRadius: '8px',
                          border: '1.5px solid #CBD5E1',
                          fontSize: '0.95rem',
                          fontWeight: 800,
                          color: '#0F172A',
                          outline: 'none',
                          fontFamily: 'monospace',
                        }}
                      />
                      <span
                        style={{
                          position: 'absolute',
                          left: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          fontSize: '0.75rem',
                          color: '#64748B',
                          fontWeight: 700,
                        }}
                      >
                        ج.م
                      </span>
                    </div>
                    {/* Quick Price Buttons */}
                    <div style={{ display: 'flex', gap: '4px', marginTop: '5px' }}>
                      {[100, 125, 150, 175, 200].map((p) => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => setEditPrice(p)}
                          style={{
                            padding: '2px 6px',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            borderRadius: '4px',
                            border: editPrice === p ? '1px solid #2563EB' : '1px solid #E2E8F0',
                            background: editPrice === p ? '#EFF6FF' : '#F8FAFC',
                            color: editPrice === p ? '#1D4ED8' : '#475569',
                            cursor: 'pointer',
                          }}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Travel Date */}
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: '4px' }}>
                      تاريخ الرحلة *
                    </label>
                    <input
                      type="date"
                      required
                      value={editDate}
                      onChange={(e) => setEditDate(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: '1.5px solid #CBD5E1',
                        fontSize: '0.88rem',
                        fontWeight: 700,
                        color: '#0F172A',
                        outline: 'none',
                        fontFamily: 'monospace',
                      }}
                    />
                  </div>

                  {/* Departure Time */}
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: '4px' }}>
                      وقت الإقلاع / التحرك *
                    </label>
                    <input
                      type="time"
                      required
                      value={editDepTime}
                      onChange={(e) => setEditDepTime(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: '1.5px solid #CBD5E1',
                        fontSize: '0.88rem',
                        fontWeight: 700,
                        color: '#0F172A',
                        outline: 'none',
                        fontFamily: 'monospace',
                      }}
                    />
                  </div>

                  {/* Arrival Time */}
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: '4px' }}>
                      وقت الوصول المتوقع
                    </label>
                    <input
                      type="time"
                      value={editArrTime}
                      onChange={(e) => setEditArrTime(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: '1.5px solid #CBD5E1',
                        fontSize: '0.88rem',
                        fontWeight: 700,
                        color: '#0F172A',
                        outline: 'none',
                        fontFamily: 'monospace',
                      }}
                    />
                  </div>
                </div>

                {/* Section 2: Direction & Route Stations */}
                <div style={{ marginBottom: '1rem', background: '#F8FAFC', padding: '12px', borderRadius: '10px', border: '1px solid #E2E8F0' }}>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                    اتجاه الرحلة:
                  </label>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                    <button
                      type="button"
                      onClick={() => setEditDirection('OUTBOUND')}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: editDirection === 'OUTBOUND' ? '2px solid #E5232A' : '1px solid #CBD5E1',
                        background: editDirection === 'OUTBOUND' ? '#FEF2F2' : '#FFFFFF',
                        color: editDirection === 'OUTBOUND' ? '#991B1B' : '#475569',
                        fontWeight: 700,
                        fontSize: '0.82rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        cursor: 'pointer',
                      }}
                    >
                      <ArrowRight size={15} />
                      <span>ذهاب للجامعة (من الإسكندرية ☀️)</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setEditDirection('RETURN')}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: editDirection === 'RETURN' ? '2px solid #2563EB' : '1px solid #CBD5E1',
                        background: editDirection === 'RETURN' ? '#EFF6FF' : '#FFFFFF',
                        color: editDirection === 'RETURN' ? '#1D4ED8' : '#475569',
                        fontWeight: 700,
                        fontSize: '0.82rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        cursor: 'pointer',
                      }}
                    >
                      <Repeat size={15} />
                      <span>عودة للإسكندرية (من الجامعة 🌙)</span>
                    </button>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.74rem', fontWeight: 700, color: '#475569', marginBottom: '4px' }}>
                        نقطة الانطلاق / محطة الركوب:
                      </label>
                      <input
                        type="text"
                        value={editOrigin}
                        onChange={(e) => setEditOrigin(e.target.value)}
                        placeholder="مثال: نفق محطة مصر / سيدي جابر"
                        style={{
                          width: '100%',
                          padding: '7px 10px',
                          borderRadius: '6px',
                          border: '1px solid #CBD5E1',
                          fontSize: '0.82rem',
                          outline: 'none',
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.74rem', fontWeight: 700, color: '#475569', marginBottom: '4px' }}>
                        الوجهة / مكان الوصول:
                      </label>
                      <input
                        type="text"
                        value={editDestination}
                        onChange={(e) => setEditDestination(e.target.value)}
                        placeholder="مثال: الحرم الجامعي"
                        style={{
                          width: '100%',
                          padding: '7px 10px',
                          borderRadius: '6px',
                          border: '1px solid #CBD5E1',
                          fontSize: '0.82rem',
                          outline: 'none',
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Section 3: Vehicle, Driver & Status */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '1rem' }}>
                  {/* Vehicle */}
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: '4px' }}>
                      المركبة المعينة:
                    </label>
                    <select
                      value={editVehicleId}
                      onChange={(e) => {
                        const vId = e.target.value;
                        setEditVehicleId(vId);
                        const v = vehicles.find((x) => x.id === vId);
                        if (v) {
                          if (v.driverName && !editDriverName) setEditDriverName(v.driverName);
                          if (v.driverPhone && !editDriverPhone) setEditDriverPhone(v.driverPhone);
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        borderRadius: '8px',
                        border: '1.5px solid #CBD5E1',
                        fontSize: '0.82rem',
                        fontWeight: 600,
                        backgroundColor: '#FFFFFF',
                      }}
                    >
                      {vehicles.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.vehicleNumber || v.plateNumber} ({v.capacity} راكب - {v.driverName || 'بدون سائق'})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Driver Name */}
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: '4px' }}>
                      اسم السائق:
                    </label>
                    <input
                      type="text"
                      value={editDriverName}
                      onChange={(e) => setEditDriverName(e.target.value)}
                      placeholder="اسم السائق المسؤول"
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        borderRadius: '8px',
                        border: '1.5px solid #CBD5E1',
                        fontSize: '0.84rem',
                        outline: 'none',
                      }}
                    />
                  </div>

                  {/* Driver Phone */}
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: '4px' }}>
                      رقم هاتف السائق:
                    </label>
                    <input
                      type="tel"
                      value={editDriverPhone}
                      onChange={(e) => setEditDriverPhone(e.target.value)}
                      placeholder="01xxxxxxxxx"
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        borderRadius: '8px',
                        border: '1.5px solid #CBD5E1',
                        fontSize: '0.84rem',
                        outline: 'none',
                        fontFamily: 'monospace',
                      }}
                    />
                  </div>

                  {/* Trip Status */}
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: '4px' }}>
                      حالة الرحلة للحجز:
                    </label>
                    <select
                      value={editStatus}
                      onChange={(e) => setEditStatus(e.target.value as TripStatus)}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        borderRadius: '8px',
                        border: '1.5px solid #CBD5E1',
                        fontSize: '0.84rem',
                        fontWeight: 700,
                        backgroundColor: '#FFFFFF',
                        color: editStatus === 'OPEN' ? '#059669' : editStatus === 'COMPLETED' ? '#2563EB' : '#DC2626',
                      }}
                    >
                      <option value="OPEN">مفتوحة للحجز (OPEN)</option>
                      <option value="FULL">مغلقة / مكتملة (FULL)</option>
                      <option value="COMPLETED">قامت وسافرت (COMPLETED)</option>
                      <option value="CANCELLED">ملغاة (CANCELLED)</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="admin-modal-footer">
                <button
                  type="button"
                  className="btn-admin-outline"
                  onClick={() => setIsEditModalOpen(false)}
                  disabled={actionLoading}
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  className="btn-admin-primary"
                  disabled={actionLoading}
                  style={{
                    backgroundColor: '#1D4ED8',
                    borderColor: '#1D4ED8',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 18px',
                    fontWeight: 700,
                  }}
                >
                  <CheckCircle2 size={16} />
                  <span>{actionLoading ? 'جاري حفظ التعديلات...' : 'حفظ التعديلات وتحديث الرحلة'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Passenger Manifest & Seat Map Modal */}
      {/* Passenger Manifest & Seat Map Modal */}
      {selectedTripManifest && (() => {
        const trip = selectedTripManifest.trip;
        const matchedVeh = vehicles.find(
          (v) =>
            v.id === trip.vehicleId ||
            v.vehicleNumber === trip.vehiclePlate ||
            v.plateNumber === trip.vehiclePlate
        );
        const tripCapacity =
          matchedVeh?.capacity ||
          trip.capacity ||
          trip.totalSeats ||
          selectedTripManifest.passengers.length ||
          50;

        const vehicleModelName =
          matchedVeh?.model ||
          (tripCapacity <= 16
            ? 'ميكروباص / تويوتا هاي اس'
            : tripCapacity <= 32
            ? 'ميني باص / كوستر'
            : 'حافلة سياحية');
        const vehiclePlateDisplay =
          matchedVeh?.vehicleNumber ||
          matchedVeh?.plateNumber ||
          trip.vehiclePlate ||
          trip.vehicleNumber ||
          'م ن ص 226';

        const bookedCount = selectedTripManifest.passengers.filter((p) => p.studentName).length;
        const availableCount = Math.max(0, tripCapacity - bookedCount);
        const boardedCount = selectedTripManifest.passengers.filter((p) => p.attendance === 'BOARDED').length;
        const layout = getVehicleSeatLayout(tripCapacity);

        return (
          <div className="admin-modal-overlay" onClick={() => setSelectedTripManifest(null)}>
            <div className="admin-modal-card manifest-modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '950px', width: '95vw' }}>
              <div className="admin-modal-header no-print">
                <div className="flex items-center gap-2">
                  <FileText size={20} className="text-primary" />
                  <div>
                    <h3 className="text-base font-bold text-slate-800">
                      منافيست وقائمة ركاب الرحلة: {trip.tripCode}
                    </h3>
                    <span className="text-xs text-slate-500">
                      {trip.universityName} — {trip.date} ({trip.departureTime})
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="btn-admin-primary text-xs flex items-center gap-1.5"
                    style={{ background: '#0F172A', color: '#FFFFFF', borderColor: '#0F172A', fontWeight: 'bold' }}
                    onClick={() => printTripManifest(trip, selectedTripManifest.passengers, { mode: 'AUTO' })}
                    title="طباعة كشف ركاب الرحلة الرسمي والمنافيست الورقي للمقاعد والركاب"
                  >
                    <Printer size={15} />
                    <span>طباعة المنافيست</span>
                  </button>
                  <button
                    type="button"
                    className="btn-admin-outline text-xs flex items-center gap-1"
                    style={{ fontSize: '11px', padding: '6px 8px' }}
                    onClick={() => printTripManifest(trip, selectedTripManifest.passengers, { mode: 'ALL_SEATS' })}
                    title={`طباعة كشف كامل مقاعد المركبة (الـ ${tripCapacity} مقعداً بالكامل)`}
                  >
                    <span>كشف الـ {tripCapacity} مقعد</span>
                  </button>
                  <button
                    type="button"
                    className="btn-admin-danger text-xs flex items-center gap-1.5"
                    style={{ background: '#FEE2E2', color: '#DC2626', borderColor: '#FCA5A5' }}
                    onClick={() => handleDeleteTrip(trip)}
                    title="حذف هذه الرحلة نهائياً"
                  >
                    <Trash2 size={14} />
                    <span>حذف الرحلة</span>
                  </button>
                  <button
                    type="button"
                    className="admin-modal-close-btn"
                    onClick={() => setSelectedTripManifest(null)}
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              <div className="admin-modal-body">
                {/* Trip & Vehicle Meta Card */}
                <div className="bg-slate-50 p-4 rounded-xl border mb-4 text-xs">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-slate-700">
                    <div>
                      <span className="text-slate-400 block mb-0.5">الجامعة الوجهة:</span>
                      <strong className="text-sm font-bold text-slate-900">{trip.universityName}</strong>
                    </div>
                    <div>
                      <span className="text-slate-400 block mb-0.5">تاريخ ووقت التحرك:</span>
                      <span className="text-sm font-mono font-bold text-slate-900">
                        {trip.date} ({trip.departureTime})
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-400 block mb-0.5">المركبة والأسطول:</span>
                      <strong className="text-sm font-bold text-slate-900">
                        {vehicleModelName} ({tripCapacity} راكباً) — {vehiclePlateDisplay}
                      </strong>
                    </div>
                    <div>
                      <span className="text-slate-400 block mb-0.5">اتجاه الرحلة:</span>
                      <span className={`dir-badge ${trip.direction === 'RETURN' ? 'return' : 'outbound'}`}>
                        {trip.direction === 'RETURN' ? 'عودة للإسكندرية' : 'ذهاب للجامعة'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Realistic Bus Graphical Layout (شكل الباص بالمقاعد) */}
                <div className="mb-6" style={{ background: '#F8FAFC', borderRadius: '20px', border: '1px solid #E2E8F0', padding: '1.25rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Bus size={20} className="text-primary" />
                      <span style={{ fontSize: '14px', fontWeight: '800', color: '#0F172A' }}>
                        مخطط مقاعد {tripCapacity <= 16 ? 'الميكروباص / هاي اس' : tripCapacity <= 32 ? 'الميني باص' : 'الحافلة السياحية'} (سعة {tripCapacity} راكباً)
                      </span>
                      <span style={{ fontSize: '12px', color: '#64748B' }}>
                        • اضغط على أي مقعد لعرض تفاصيل الحجز أسفل المركبة
                      </span>
                    </div>

                    {/* Color Legend Indicators */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px', fontWeight: '700' }}>
                      {/* Yellow: Available */}
                      <span
                        style={{
                          background: '#FEF08A',
                          color: '#713F12',
                          border: '1.5px solid #EAB308',
                          padding: '4px 10px',
                          borderRadius: '8px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        <span style={{ width: '10px', height: '10px', borderRadius: '3px', background: '#EAB308' }} />
                        <span>متاح ({availableCount})</span>
                      </span>

                      {/* Red: Occupied */}
                      <span
                        style={{
                          background: '#FEE2E2',
                          color: '#991B1B',
                          border: '1.5px solid #EF4444',
                          padding: '4px 10px',
                          borderRadius: '8px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        <span style={{ width: '10px', height: '10px', borderRadius: '3px', background: '#EF4444' }} />
                        <span>مشغول ({bookedCount})</span>
                      </span>

                      {/* Green: Boarded */}
                      <span
                        style={{
                          background: '#DCFCE7',
                          color: '#15803D',
                          border: '1.5px solid #22C55E',
                          padding: '4px 10px',
                          borderRadius: '8px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        <span style={{ width: '10px', height: '10px', borderRadius: '3px', background: '#22C55E' }} />
                        <span>صعد للحافلة ({boardedCount})</span>
                      </span>
                    </div>
                  </div>

                {/* THE BUS SHELL (شكل الباص بالمقاعد) */}
                <div
                  style={{
                    background: 'linear-gradient(180deg, #1E293B 0%, #0F172A 100%)',
                    borderRadius: '44px 44px 28px 28px',
                    border: '4px solid #334155',
                    boxShadow: '0 20px 45px -10px rgba(15, 23, 42, 0.4)',
                    padding: '16px 14px 20px 14px',
                    maxWidth: '460px',
                    margin: '0 auto',
                    position: 'relative',
                    color: '#fff',
                  }}
                >
                  {/* Exterior Side Mirrors */}
                  <div
                    style={{
                      position: 'absolute',
                      top: '38px',
                      right: '-12px',
                      width: '12px',
                      height: '34px',
                      background: '#334155',
                      borderRadius: '6px 0 0 6px',
                      boxShadow: '-2px 2px 4px rgba(0,0,0,0.3)',
                    }}
                    title="مرآة جانبية يمنى"
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: '38px',
                      left: '-12px',
                      width: '12px',
                      height: '34px',
                      background: '#334155',
                      borderRadius: '0 6px 6px 0',
                      boxShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                    }}
                    title="مرآة جانبية يسرى"
                  />

                  {/* Front Windshield Glass */}
                  <div
                    style={{
                      background: 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)',
                      borderRadius: '34px 34px 10px 10px',
                      padding: '10px 14px',
                      border: '2px solid rgba(255, 255, 255, 0.1)',
                      marginBottom: '12px',
                      textAlign: 'center',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                      <span style={{ width: '14px', height: '7px', background: '#FACC15', borderRadius: '3px', boxShadow: '0 0 8px #FACC15' }} />
                      <span style={{ fontSize: '11px', fontWeight: '800', color: '#94A3B8', letterSpacing: '0.5px' }}>
                        مقدمة {tripCapacity <= 16 ? 'المركبة / هاي اس' : tripCapacity <= 32 ? 'الميني باص' : 'الحافلة'} (الزجاج الأمامي)
                      </span>
                      <span style={{ width: '14px', height: '7px', background: '#FACC15', borderRadius: '3px', boxShadow: '0 0 8px #FACC15' }} />
                    </div>
                    <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: '8px', padding: '4px 8px', fontSize: '12px', color: '#38BDF8', fontWeight: '700' }}>
                      🚌 {trip.universityName} • {trip.date} ({trip.departureTime})
                    </div>
                  </div>

                  {/* Front Cockpit: Driver on Left, Door on Right */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 12px',
                      background: 'rgba(255, 255, 255, 0.04)',
                      borderRadius: '12px',
                      marginBottom: '12px',
                      border: '1px dashed rgba(255, 255, 255, 0.15)',
                    }}
                  >
                    {/* Driver Cab (Left) */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '10px',
                          background: '#334155',
                          border: '2px solid #64748B',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#F1F5F9" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                          <path d="M2 12h20" />
                        </svg>
                      </div>
                      <div>
                        <span style={{ display: 'block', fontSize: '11px', fontWeight: '800', color: '#F1F5F9' }}>السائق</span>
                        <span style={{ fontSize: '10px', color: '#94A3B8' }}>كابينة القيادة</span>
                      </div>
                    </div>

                    {/* Central Aisle Heading */}
                    <span style={{ fontSize: '11px', color: '#64748B', fontWeight: '600' }}>|| الممر ||</span>

                    {/* Boarding Door (Right) */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', textAlign: 'left' }}>
                      <div>
                        <span style={{ display: 'block', fontSize: '11px', fontWeight: '800', color: '#4ADE80' }}>باب الصعود</span>
                        <span style={{ fontSize: '10px', color: '#94A3B8' }}>
                          الدخول {tripCapacity <= 16 ? 'للمركبة' : 'للحافلة'}
                        </span>
                      </div>
                      <div
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '10px',
                          background: 'rgba(34, 197, 94, 0.15)',
                          border: '2px solid #22C55E',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <ArrowRight size={20} color="#4ADE80" />
                      </div>
                    </div>
                  </div>

                  {/* Seat Rendering Function */}
                  {(() => {
                    const renderSeatItem = (seatNum: number) => {
                      const p = selectedTripManifest.passengers.find((item) => item.seatNumber === seatNum);
                      const isBooked = Boolean(p?.studentName);
                      const isSelected = selectedManifestSeat === seatNum;
                      const isBoarded = p?.attendance === 'BOARDED';

                      return (
                        <button
                          key={seatNum}
                          type="button"
                          onClick={() => {
                            setSelectedManifestSeat(seatNum);
                            const el = document.getElementById(`manifest-seat-row-${seatNum}`);
                            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }}
                          title={`المقعد #${seatNum}: ${isBooked ? `${p?.studentName} (${p?.pickupStation || 'المحطة'})` : 'مقعد متاح شاغر'}`}
                          style={{
                            width: '56px',
                            height: '56px',
                            borderRadius: '12px',
                            border: isSelected
                              ? '3px solid #3B82F6'
                              : isBooked
                              ? '2px solid #B91C1C'
                              : '2px solid #CA8A04',
                            background: isBooked
                              ? 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)'
                              : 'linear-gradient(135deg, #FEF08A 0%, #FACC15 100%)',
                            color: isBooked ? '#FFFFFF' : '#713F12',
                            cursor: 'pointer',
                            padding: '3px 4px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            transition: 'all 0.15s ease',
                            transform: isSelected ? 'scale(1.08)' : 'scale(1)',
                            boxShadow: isSelected
                              ? '0 0 0 3px rgba(59, 130, 246, 0.5), 0 8px 16px rgba(0,0,0,0.3)'
                              : isBooked
                              ? '0 2px 4px rgba(220, 38, 38, 0.25)'
                              : '0 2px 4px rgba(202, 138, 4, 0.2)',
                            position: 'relative',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', fontSize: '10px', fontWeight: '800' }}>
                            <span>#{seatNum}</span>
                            {isBoarded ? (
                              <span style={{ background: '#10B981', color: '#fff', borderRadius: '4px', padding: '1px 3px', fontSize: '8px' }}>صعد</span>
                            ) : (
                              <span style={{ fontSize: '9px', opacity: 0.9 }}>{isBooked ? 'مشغول' : 'متاح'}</span>
                            )}
                          </div>

                          <Armchair size={18} strokeWidth={2.2} style={{ opacity: isBooked ? 1 : 0.85 }} />

                          <span
                            style={{
                              fontSize: '9.5px',
                              fontWeight: '700',
                              maxWidth: '50px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'block',
                              lineHeight: '1.1',
                            }}
                          >
                            {isBooked ? p?.studentName?.split(' ')[0] || 'محجوز' : 'شاغر'}
                          </span>
                        </button>
                      );
                    };

                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                        {/* Dynamic Vehicle Rows */}
                        {layout.rows.map((rowItem) => (
                          <div
                            key={rowItem.rowNum}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              background: 'rgba(255, 255, 255, 0.03)',
                              padding: '4px 6px',
                              borderRadius: '10px',
                            }}
                          >
                            {/* Left Side */}
                            <div style={{ display: 'flex', gap: '5px' }}>
                              {rowItem.left.map((s) => renderSeatItem(s))}
                            </div>

                            {/* Central Aisle */}
                            <div style={{ textAlign: 'center', minWidth: '36px' }}>
                              <span style={{ fontSize: '9px', fontWeight: '800', color: '#64748B' }}>
                                {rowItem.rowNum}
                              </span>
                              <div
                                style={{
                                  width: '2px',
                                  height: '24px',
                                  background: 'repeating-linear-gradient(to bottom, #475569 0, #475569 4px, transparent 4px, transparent 8px)',
                                  margin: '2px auto 0 auto',
                                }}
                              />
                            </div>

                            {/* Right Side */}
                            <div style={{ display: 'flex', gap: '5px' }}>
                              {rowItem.right.map((s) => renderSeatItem(s))}
                            </div>
                          </div>
                        ))}

                        {/* Rear Bench */}
                        {layout.rearBench.length > 0 && (
                          <div
                            style={{
                              marginTop: '6px',
                              padding: '8px 12px',
                              background: 'rgba(255, 255, 255, 0.06)',
                              borderRadius: '12px',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                            }}
                          >
                            <div style={{ fontSize: '10px', fontWeight: '800', color: '#94A3B8' }}>
                              الكنبة الخلفية
                            </div>
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'center' }}>
                              {layout.rearBench.map((s) => renderSeatItem(s))}
                            </div>
                            <div style={{ fontSize: '10px', color: '#64748B' }}>
                              مؤخرة المركبة
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* BOOKING DETAILS SECTION DIRECTLY BELOW THE BUS (تفصيل الحجز أسفل الباص) */}
              <div style={{ marginTop: '1.5rem', borderTop: '2px dashed #E2E8F0', paddingTop: '1.5rem' }}>
                {/* Spotlight Card: If a specific seat is clicked on the bus */}
                {selectedManifestSeat !== null && (
                  (() => {
                    const selP = selectedTripManifest.passengers.find((p) => p.seatNumber === selectedManifestSeat);
                    const isBooked = Boolean(selP?.studentName);

                    return (
                      <div
                        style={{
                          background: isBooked ? '#FEF2F2' : '#FEFCE8',
                          border: '2px solid',
                          borderColor: isBooked ? '#FCA5A5' : '#FEF08A',
                          borderRadius: '16px',
                          padding: '1.25rem',
                          marginBottom: '1.5rem',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div
                              style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '12px',
                                background: isBooked ? '#EF4444' : '#EAB308',
                                color: isBooked ? '#fff' : '#713F12',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: '800',
                                fontSize: '16px',
                              }}
                            >
                              #{selectedManifestSeat}
                            </div>
                            <div>
                              <span style={{ fontSize: '12px', fontWeight: '700', color: isBooked ? '#B91C1C' : '#854D0E', display: 'block' }}>
                                {isBooked ? '🔴 مقعد محجوز (تفاصيل التذكرة)' : '🟡 مقعد متاح وشاغر للحجز'}
                              </span>
                              <h4 style={{ margin: '2px 0 0 0', fontSize: '1.1rem', fontWeight: '800', color: '#0F172A' }}>
                                {isBooked ? selP?.studentName : `المقعد رقم ${selectedManifestSeat} متاح للركوب`}
                              </h4>
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() => setSelectedManifestSeat(null)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: '#64748B',
                              cursor: 'pointer',
                              padding: '4px',
                            }}
                          >
                            <X size={18} />
                          </button>
                        </div>

                        {isBooked && selP && (
                          <div style={{ marginTop: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', fontSize: '12px' }}>
                            <div style={{ background: '#fff', padding: '8px 12px', borderRadius: '10px', border: '1px solid #FECACA' }}>
                              <span style={{ color: '#64748B', display: 'block' }}>الهاتف / واتساب:</span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '3px' }}>
                                <strong dir="ltr">{selP.studentPhone}</strong>
                                {selP.studentPhone && selP.studentPhone !== '—' && (
                                  <>
                                    <a
                                      href={`https://wa.me/${selP.studentPhone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`مرحباً ${selP.studentName}، نؤكد على موعد رحلتك لـ ${selectedTripManifest.trip.universityName}...`)}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="btn-admin-icon-wa"
                                      title="واتساب"
                                    >
                                      <MessageSquare size={13} />
                                    </a>
                                    <a href={`tel:${selP.studentPhone}`} className="btn-admin-icon-call" title="اتصال">
                                      <Phone size={13} />
                                    </a>
                                  </>
                                )}
                              </div>
                            </div>

                            <div style={{ background: '#fff', padding: '8px 12px', borderRadius: '10px', border: '1px solid #FECACA' }}>
                              <span style={{ color: '#64748B', display: 'block' }}>محطة الركوب والانطلاق:</span>
                              <strong style={{ color: '#0F172A', marginTop: '3px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <MapPin size={13} className="text-primary" />
                                {selP.pickupStation || 'المحطة المحددة'}
                              </strong>
                            </div>

                            <div style={{ background: '#fff', padding: '8px 12px', borderRadius: '10px', border: '1px solid #FECACA' }}>
                              <span style={{ color: '#64748B', display: 'block' }}>كود الحجز والتذكرة:</span>
                              <strong style={{ color: '#0F172A', marginTop: '3px', display: 'block', fontFamily: 'monospace' }}>
                                {selP.bookingCode} ({selP.ticketId || '—'})
                              </strong>
                            </div>

                            <div style={{ background: '#fff', padding: '8px 12px', borderRadius: '10px', border: '1px solid #FECACA', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <div>
                                <span style={{ color: '#64748B', display: 'block' }}>حالة الحضور:</span>
                                <strong style={{ color: selP.attendance === 'BOARDED' ? '#16A34A' : selP.attendance === 'NO_SHOW' ? '#DC2626' : '#D97706' }}>
                                  {selP.attendance === 'BOARDED' ? 'صعد للحافلة ✓' : selP.attendance === 'NO_SHOW' ? 'غائب ✕' : 'بانتظار الصعود...'}
                                </strong>
                              </div>
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <button
                                  type="button"
                                  onClick={() => handleToggleAttendance(selP.seatNumber, 'BOARDED')}
                                  style={{
                                    background: selP.attendance === 'BOARDED' ? '#16A34A' : '#DCFCE7',
                                    color: selP.attendance === 'BOARDED' ? '#fff' : '#15803D',
                                    border: 'none',
                                    padding: '4px 8px',
                                    borderRadius: '6px',
                                    fontSize: '11px',
                                    cursor: 'pointer',
                                    fontWeight: '700',
                                  }}
                                >
                                  صعد
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleToggleAttendance(selP.seatNumber, 'NO_SHOW')}
                                  style={{
                                    background: selP.attendance === 'NO_SHOW' ? '#DC2626' : '#FEE2E2',
                                    color: selP.attendance === 'NO_SHOW' ? '#fff' : '#B91C1C',
                                    border: 'none',
                                    padding: '4px 8px',
                                    borderRadius: '6px',
                                    fontSize: '11px',
                                    cursor: 'pointer',
                                    fontWeight: '700',
                                  }}
                                >
                                  غائب
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()
                )}

                {/* Filter and Search Bar */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Users size={18} className="text-primary" />
                    <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '800', color: '#0F172A' }}>
                      تفصيل الحجوزات وقائمة ركاب الرحلة (Check-in Manifest):
                    </h4>
                  </div>

                  {/* Filter Tabs */}
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={() => setManifestFilter('ALL')}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '8px',
                        border: '1px solid',
                        borderColor: manifestFilter === 'ALL' ? '#E5232A' : '#CBD5E1',
                        background: manifestFilter === 'ALL' ? '#FEE2E2' : '#fff',
                        color: manifestFilter === 'ALL' ? '#E5232A' : '#475569',
                        fontSize: '12px',
                        cursor: 'pointer',
                        fontWeight: '700',
                      }}
                    >
                      الكل ({tripCapacity})
                    </button>
                    <button
                      type="button"
                      onClick={() => setManifestFilter('BOOKED')}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '8px',
                        border: '1px solid',
                        borderColor: manifestFilter === 'BOOKED' ? '#DC2626' : '#FECACA',
                        background: manifestFilter === 'BOOKED' ? '#EF4444' : '#FEF2F2',
                        color: manifestFilter === 'BOOKED' ? '#fff' : '#DC2626',
                        fontSize: '12px',
                        cursor: 'pointer',
                        fontWeight: '700',
                      }}
                    >
                      المحجوزين بالأحمر ({bookedCount})
                    </button>
                    <button
                      type="button"
                      onClick={() => setManifestFilter('AVAILABLE')}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '8px',
                        border: '1px solid',
                        borderColor: manifestFilter === 'AVAILABLE' ? '#CA8A04' : '#FEF08A',
                        background: manifestFilter === 'AVAILABLE' ? '#EAB308' : '#FEFCE8',
                        color: manifestFilter === 'AVAILABLE' ? '#713F12' : '#854D0E',
                        fontSize: '12px',
                        cursor: 'pointer',
                        fontWeight: '700',
                      }}
                    >
                      المتاحة بالأصفر ({availableCount})
                    </button>
                    <button
                      type="button"
                      onClick={() => setManifestFilter('BOARDED')}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '8px',
                        border: '1px solid',
                        borderColor: manifestFilter === 'BOARDED' ? '#16A34A' : '#BBF7D0',
                        background: manifestFilter === 'BOARDED' ? '#22C55E' : '#F0FDF4',
                        color: manifestFilter === 'BOARDED' ? '#fff' : '#15803D',
                        fontSize: '12px',
                        cursor: 'pointer',
                        fontWeight: '700',
                      }}
                    >
                      صعد للحافلة ({boardedCount})
                    </button>
                  </div>
                </div>

                {/* Instant Search in Manifest */}
                <div style={{ marginBottom: '1rem', position: 'relative' }}>
                  <Search size={16} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94A3B8' }} />
                  <input
                    type="text"
                    placeholder="بحث سريع في كشف الركاب بالاسم أو رقم المقعد أو الهاتف أو محطة الركوب..."
                    value={manifestSearchTerm}
                    onChange={(e) => setManifestSearchTerm(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '9px 38px 9px 12px',
                      borderRadius: '10px',
                      border: '1px solid #CBD5E1',
                      fontSize: '13px',
                      outline: 'none',
                    }}
                  />
                </div>

                {/* The Comprehensive Passenger Table */}
                <div className="admin-table-wrapper" style={{ maxHeight: '380px', overflowY: 'auto' }}>
                  <table className="admin-data-table text-xs">
                    <thead>
                      <tr>
                        <th style={{ width: '60px' }}>المقعد</th>
                        <th>اسم الطالب</th>
                        <th>رقم الهاتف والتواصل</th>
                        <th>محطة الركوب والانطلاق</th>
                        <th>كود الحجز والتذكرة</th>
                        <th>حالة السداد</th>
                        <th>حالة الصعود (Check-in)</th>
                        <th>إجراءات التحضير</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedTripManifest.passengers
                        .filter((p) => {
                          const isBooked = Boolean(p.studentName);
                          if (manifestFilter === 'BOOKED' && !isBooked) return false;
                          if (manifestFilter === 'AVAILABLE' && isBooked) return false;
                          if (manifestFilter === 'BOARDED' && p.attendance !== 'BOARDED') return false;
                          if (manifestFilter === 'NO_SHOW' && p.attendance !== 'NO_SHOW') return false;

                          if (manifestSearchTerm.trim()) {
                            const term = manifestSearchTerm.trim().toLowerCase();
                            const matchSeat = String(p.seatNumber).includes(term);
                            const matchName = (p.studentName || '').toLowerCase().includes(term);
                            const matchPhone = (p.studentPhone || '').includes(term);
                            const matchStation = (p.pickupStation || '').toLowerCase().includes(term);
                            const matchCode = (p.bookingCode || '').toLowerCase().includes(term);
                            return matchSeat || matchName || matchPhone || matchStation || matchCode;
                          }
                          return true;
                        })
                        .map((p) => {
                          const isBooked = Boolean(p.studentName);
                          const cleanPhone = (p.studentPhone || '').replace(/[^0-9]/g, '');
                          const waNumber = cleanPhone.startsWith('0') ? '2' + cleanPhone : cleanPhone;
                          const isHighlighted = selectedManifestSeat === p.seatNumber;

                          return (
                            <tr
                              key={p.seatNumber}
                              id={`manifest-seat-row-${p.seatNumber}`}
                              style={{
                                background: isHighlighted
                                  ? '#EFF6FF'
                                  : isBooked
                                  ? '#FFF5F5'
                                  : '#FFFFF0',
                                borderRight: isHighlighted ? '4px solid #2563EB' : undefined,
                                transition: 'background 0.15s ease',
                              }}
                            >
                              <td>
                                <span
                                  style={{
                                    fontFamily: 'monospace',
                                    fontWeight: '800',
                                    padding: '4px 8px',
                                    borderRadius: '6px',
                                    display: 'inline-block',
                                    background: isBooked ? '#EF4444' : '#FEF08A',
                                    color: isBooked ? '#FFFFFF' : '#713F12',
                                    border: isBooked ? '1px solid #B91C1C' : '1px solid #CA8A04',
                                  }}
                                >
                                  #{p.seatNumber}
                                </span>
                              </td>
                              <td>
                                {isBooked ? (
                                  <strong className="text-slate-900 block font-bold">{p.studentName}</strong>
                                ) : (
                                  <span style={{ color: '#854D0E', fontWeight: '600' }}>🟡 مقعد متاح للحجز</span>
                                )}
                              </td>
                              <td>
                                {isBooked && p.studentPhone && p.studentPhone !== '—' ? (
                                  <div className="flex items-center gap-1.5">
                                    <span className="font-mono text-slate-700" dir="ltr">
                                      {p.studentPhone}
                                    </span>
                                    {cleanPhone && (
                                      <a
                                        href={`https://wa.me/${waNumber}?text=${encodeURIComponent(
                                          `مرحباً ${p.studentName}، نؤكد على موعد رحلتك لـ ${selectedTripManifest.trip.universityName} اليوم...`
                                        )}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="btn-admin-icon-wa"
                                        title="مراسلة واتساب فورية"
                                      >
                                        <MessageSquare size={12} />
                                      </a>
                                    )}
                                    {cleanPhone && (
                                      <a href={`tel:${cleanPhone}`} className="btn-admin-icon-call" title="اتصال هاتفي">
                                        <Phone size={12} />
                                      </a>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-slate-400">—</span>
                                )}
                              </td>
                              <td>
                                {isBooked ? (
                                  <span style={{ color: '#0F172A', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <MapPin size={12} className="text-primary" />
                                    {p.pickupStation || 'المحطة المحددة'}
                                  </span>
                                ) : (
                                  <span className="text-slate-400">—</span>
                                )}
                              </td>
                              <td>
                                {isBooked ? (
                                  <div>
                                    <span className="font-mono font-bold text-slate-900 block text-xs">
                                      {p.ticketId || p.bookingCode}
                                    </span>
                                    <span className="text-[10px] text-slate-500 block font-mono">
                                      كود: {p.bookingCode}
                                    </span>
                                  </div>
                                ) : (
                                  <span className="text-slate-400">—</span>
                                )}
                              </td>
                              <td>
                                {isBooked ? (
                                  <span
                                    className={`status-pill ${
                                      p.paymentStatus === 'PAID' ? 'confirmed' : 'pending'
                                    }`}
                                  >
                                    {p.paymentStatus === 'PAID' ? 'تم السداد' : 'بانتظار السداد'}
                                  </span>
                                ) : (
                                  <span className="text-slate-400">—</span>
                                )}
                              </td>
                              <td>
                                {isBooked ? (
                                  <span
                                    className={`status-pill ${
                                      p.attendance === 'BOARDED'
                                        ? 'confirmed'
                                        : p.attendance === 'NO_SHOW'
                                        ? 'cancelled'
                                        : 'pending'
                                    }`}
                                  >
                                    {p.attendance === 'BOARDED'
                                      ? 'صعد للحافلة ✓'
                                      : p.attendance === 'NO_SHOW'
                                      ? 'تغيب (No-Show) ✕'
                                      : 'بانتظار الصعود...'}
                                  </span>
                                ) : (
                                  <span style={{ color: '#A16207', fontSize: '11px' }}>شاغر</span>
                                )}
                              </td>
                              <td>
                                {isBooked ? (
                                  <div className="flex items-center gap-1">
                                    <button
                                      type="button"
                                      className={`btn-admin-success text-xs py-1 px-2 ${
                                        p.attendance === 'BOARDED' ? 'opacity-50' : ''
                                      }`}
                                      onClick={() => handleToggleAttendance(p.seatNumber, 'BOARDED')}
                                      title="تأكيد الصعود"
                                    >
                                      <span>صعد</span>
                                    </button>
                                    <button
                                      type="button"
                                      className={`btn-admin-danger text-xs py-1 px-2 ${
                                        p.attendance === 'NO_SHOW' ? 'opacity-50' : ''
                                      }`}
                                      onClick={() => handleToggleAttendance(p.seatNumber, 'NO_SHOW')}
                                      title="تأكيد الغياب"
                                    >
                                      <span>غائب</span>
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => setSelectedManifestSeat(p.seatNumber)}
                                    style={{
                                      background: '#FEF08A',
                                      border: '1px solid #CA8A04',
                                      color: '#713F12',
                                      fontSize: '11px',
                                      padding: '3px 8px',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      fontWeight: '600',
                                    }}
                                  >
                                    تحديد
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="admin-modal-footer no-print">
              <button
                type="button"
                className="btn-admin-primary"
                onClick={() => setSelectedTripManifest(null)}
              >
                إغلاق المنافيست
              </button>
            </div>
          </div>
        </div>
      );
    })()}

      {/* Bulk Route Trips Management Modal */}
      <BulkRouteTripsModal
        isOpen={isBulkModalOpen}
        onClose={() => setIsBulkModalOpen(false)}
        trips={trips}
        universities={universities}
        vehicles={vehicles.length > 0 ? vehicles : SEED_VEHICLES}
        onSuccess={(msg) => {
          setMessage({ text: msg, type: 'success' });
          setTimeout(() => setMessage(null), 6000);
        }}
        onRefresh={() => loadData(true)}
      />
    </div>
  );
};
